import * as BABYLON from "babylonjs";
// import "babylonjs-inspector";
import "babylonjs-loaders";
import { CharacterController } from "babylonjs-charactercontroller";

let engine;
let cc;
let gl;
let pipeline;
let resizeTimeout;
let callbackDisplayHotspot;
let callbackChangePerspective;

let pendingOperations = [];
let canvasBJS;
const BASE_URL = process.env.BASE_URL;

let lookingAt = null;

const playerPositions = {
  Chill_Lab: [4.78, 0, -3.15],
  Indoor_Lab: [1.4, 0, -3.27],
};

const hostPositions = {
  Chill_Lab: [2, 0, -4.274],
  Indoor_Lab: [-0.9, 0, -4.4],
};

const helperCameraTargets = {
  Chill_Lab: [8, 1.5, -3.5],
  Indoor_Lab: [4.325, 1.5, -3.3],
};

const welcomeCameraTargets = {
  Chill_Lab: [3.7, 1.5, -4],
  Indoor_Lab: [0.37, 1.5, -3.79],
};

const securePlayerPointss = {
  Chill_Lab: {
    starting1: [8, -2],
    starting2: [-6.5, -4],
    starting3: [-6.5, 4],
    starting4: [6.5, 4],
  },
  Indoor_Lab: {
    starting1: [7, 6],
    starting2: [-6.5, -4],
    starting3: [-6.5, 4],
    starting4: [6.5, 4],
  },
};

const rotationPlayerTest = {
  Chill_Lab: {
    rotation: -75,
    alphaD: -15,
    betaD: 82,
  },
  Indoor_Lab: {
    rotation: 225,
    alphaD: 45,
    betaD: 90,
  },
};

const playerRotation = new BABYLON.Vector3(0, (100 * Math.PI) / 180, 0); // (90 * Math.PI) / 180

let playerNameGlobal;
let player;
let host;
let lab;
let product;

let initialPositions = {};

const createScene = async ({
  displayHotspot,
  changePerspectiveCk,
  canvas,
  model,
}) => {
  const uniqueID = generateUniqueID();
  pendingOperations.push(uniqueID);

  if (displayHotspot) {
    callbackDisplayHotspot = displayHotspot;
  }

  if (changePerspectiveCk) {
    callbackChangePerspective = changePerspectiveCk;
  }

  canvasBJS = canvas;
  lab = model;

  engine = new BABYLON.Engine(canvasBJS, false);

  window.scene = new BABYLON.Scene(engine);
  window.scene.clearColor = BABYLON.Color3.Black();

  gl = new BABYLON.GlowLayer("glow", window.scene);

  loadEnvironment();

  const meshPath = `${BASE_URL}models/${lab}/`;

  const assetsManager = new BABYLON.AssetsManager(window.scene);
  assetsManager.useDefaultLoadingScreen = false;
  const mainTask = assetsManager.addMeshTask(
    "mainTask",
    "",
    meshPath,
    `${lab}.gltf`
  );

  await new Promise((resolve, reject) => {
    mainTask.onError = (task, message, exception) => {
      console.error("Scene", message, exception, task);
      reject({ message, exception, task });
    };

    assetsManager.onFinish = async () => {
      processMeshes();
      window.scene.cleanCachedTextureBuffer();
      window.scene.blockMaterialDirtyMechanism = false;
      resolve();
    };

    assetsManager.load();
  });

  createColliders();

  createMainCamera();
  createFlyCamera();
  createHelperCamera();
  createWelcomeCamera();

  setCamera("Welcome");

  createRenderingPipeline();

  await loadHelper();

  pendingOperations = pendingOperations.filter((id) => id !== uniqueID);
};

function createColliders() {
  if (lab === "Chill_Lab") {
    const boxProduct_AFC_NA = BABYLON.MeshBuilder.CreateBox(
      "boxProduct_AFC_NA",
      {
        height: 6,
        width: 9, // 7.5
        depth: 4.25, // 2.5
      }
    );

    boxProduct_AFC_NA.renderingGroupId = 1;
    boxProduct_AFC_NA.position = new BABYLON.Vector3(0.5, 0, 0);
    boxProduct_AFC_NA.checkCollisions = true;
    boxProduct_AFC_NA.isPickable = true;
    boxProduct_AFC_NA.isVisible = false;

    boxProduct_AFC_NA.setEnabled(false);

    const boxProduct_AFC_AD = BABYLON.MeshBuilder.CreateBox(
      "boxProduct_AFC_AD",
      {
        height: 6,
        width: 10, // 8.5 10.2
        depth: 4.86, // 2.7
      }
    );

    boxProduct_AFC_AD.renderingGroupId = 1;
    boxProduct_AFC_AD.position = new BABYLON.Vector3(-0.2, 0, 0);
    boxProduct_AFC_AD.checkCollisions = true;
    boxProduct_AFC_AD.isPickable = true;
    boxProduct_AFC_AD.isVisible = false;

    boxProduct_AFC_AD.setEnabled(false);

    const boxRails = BABYLON.MeshBuilder.CreateBox("boxRails", {
      height: 6,
      width: 7.5,
      depth: 2.5,
    });

    boxRails.renderingGroupId = 1;
    boxRails.position = new BABYLON.Vector3(7, 0, 0);
    boxRails.checkCollisions = true;
    boxRails.isPickable = true;
    boxRails.isVisible = false;

    boxRails.setEnabled(false);

    const boxPipes_AFC_NA = BABYLON.MeshBuilder.CreateBox("boxPipes_AFC_NA", {
      height: 6,
      width: 3,
      depth: 1.5,
    });

    boxPipes_AFC_NA.renderingGroupId = 1;
    boxPipes_AFC_NA.position = new BABYLON.Vector3(-0.215, 0, -1.6);
    boxPipes_AFC_NA.checkCollisions = true;
    boxPipes_AFC_NA.isPickable = true;
    boxPipes_AFC_NA.isVisible = false;

    boxPipes_AFC_NA.setEnabled(false);

    const boxPipes_AFC_AD = BABYLON.MeshBuilder.CreateBox("boxPipes_AFC_AD", {
      height: 6,
      width: 1.7,
      depth: 0.5,
    });

    boxPipes_AFC_AD.renderingGroupId = 1;
    boxPipes_AFC_AD.position = new BABYLON.Vector3(-4.5, 0, -1);
    boxPipes_AFC_AD.checkCollisions = true;
    boxPipes_AFC_AD.isPickable = true;
    boxPipes_AFC_AD.isVisible = false;

    boxPipes_AFC_AD.setEnabled(false);
  } else {
    const boxRails = BABYLON.MeshBuilder.CreateBox("boxRails", {
      height: 3,
      width: 3,
      depth: 7.5,
    });

    boxRails.renderingGroupId = 1;
    boxRails.position = new BABYLON.Vector3(0, 1.5, 5.5);
    boxRails.checkCollisions = true;
    boxRails.isPickable = true;
    boxRails.isVisible = false;

    boxRails.setEnabled(false);

    const boxProduct_CWA = BABYLON.MeshBuilder.CreateBox("boxProduct_CWA", {
      height: 2.5,
      width: 10.2, // 8.5
      depth: 5.22, // 3.5
    });

    boxProduct_CWA.renderingGroupId = 1;
    boxProduct_CWA.position = new BABYLON.Vector3(-3, 1.25, 0.3);
    boxProduct_CWA.checkCollisions = true;
    boxProduct_CWA.isPickable = true;
    boxProduct_CWA.isVisible = false;

    boxProduct_CWA.setEnabled(false);

    const boxProduct_PDX = BABYLON.MeshBuilder.CreateBox("boxProduct_PDX", {
      height: 2.5,
      width: 10.2, // 8.5
      depth: 5.22, // 3.5
    });

    boxProduct_PDX.renderingGroupId = 1;
    boxProduct_PDX.position = new BABYLON.Vector3(-3, 1.25, 0.3);
    boxProduct_PDX.checkCollisions = true;
    boxProduct_PDX.isPickable = true;
    boxProduct_PDX.isVisible = false;

    boxProduct_PDX.setEnabled(false);

    const boxHeatExchanger = BABYLON.MeshBuilder.CreateBox(
      "boxHeatExchanger_PDX",
      {
        height: 3,
        width: 1,
        depth: 1,
      }
    );

    boxHeatExchanger.renderingGroupId = 1;
    boxHeatExchanger.position = new BABYLON.Vector3(3.1, 1.5, 2);
    boxHeatExchanger.checkCollisions = true;
    boxHeatExchanger.isPickable = true;
    boxHeatExchanger.isVisible = false;

    boxHeatExchanger.setEnabled(false);
  }
}

function createHotspot(
  name,
  size = 1,
  [x, y, z] = [0, 0, 0],
  callbackIn,
  callbackOut,
  callbackClick
) {
  const hotspotIcon = `${BASE_URL}hotspots/${name}.png`;

  const matHotspot = new BABYLON.StandardMaterial(
    `M_hotspot_${name}`,
    window.scene
  );

  matHotspot.transparencyMode = BABYLON.Material.MATERIAL_ALPHATEST;
  matHotspot.diffuseTexture = new BABYLON.Texture(hotspotIcon, window.scene);
  matHotspot.useAlphaFromDiffuseTexture = true;
  matHotspot.diffuseTexture.hasAlpha = true;
  matHotspot.disableLighting = true;
  matHotspot.lightmapTexture = new BABYLON.Texture(hotspotIcon, window.scene);

  const position = new BABYLON.Vector3(x, y, z);
  const scaling = new BABYLON.Vector3(0.025 * size, 0.025 * size, 0.025 * size);

  const hotspotPlane = BABYLON.MeshBuilder.CreatePlane(
    `hotspot_${name}`,
    {
      size: 10,
    },
    window.scene
  );

  hotspotPlane.renderingGroupId = 1;
  hotspotPlane.visibility = 0;
  hotspotPlane.setAbsolutePosition(position);
  hotspotPlane.scaling = scaling;
  hotspotPlane.billboardMode = BABYLON.Mesh.BILLBOARDMODE_ALL;

  hotspotPlane.material = matHotspot;

  hotspotPlane.actionManager = new BABYLON.ActionManager(window.scene);

  if (callbackIn) {
    hotspotPlane.actionManager.registerAction(
      new BABYLON.ExecuteCodeAction(
        BABYLON.ActionManager.OnPointerOverTrigger,
        (event) => {
          const { right, bottom, width } = getPosition(hotspotPlane);
          callbackIn(name, right, bottom, width, event);
        }
      )
    );
  }

  if (callbackOut) {
    hotspotPlane.actionManager.registerAction(
      new BABYLON.ExecuteCodeAction(
        BABYLON.ActionManager.OnPointerOutTrigger,
        (event) => {
          callbackOut(name, event);
        }
      )
    );
  }

  if (callbackClick) {
    hotspotPlane.actionManager.registerAction(
      new BABYLON.ExecuteCodeAction(
        BABYLON.ActionManager.OnPickTrigger,
        (event) => {
          callbackClick(name, event);
        }
      )
    );
  }
}

function updateHotspotPosition(key, [x, y, z] = [0, 0, 0]) {
  const position = new BABYLON.Vector3(x, y, z);

  const hotspotPlane = window.scene.getMeshByName(`hotspot_${key}`);

  hotspotPlane.position = position;
}

function getPosition(hotspot) {
  let hotspotPlane = hotspot;
  if (typeof hotspot === "string") {
    hotspotPlane = window.scene.getMeshByName(`hotspot_${hotspot}`);
  }

  let renderWidth = engine.getRenderWidth();
  let renderHeight = engine.getRenderHeight();

  renderWidth *= engine.getHardwareScalingLevel();
  renderHeight *= engine.getHardwareScalingLevel();

  const sceneTransformMatrix = window.scene.getTransformMatrix();

  const viewport = window.scene.activeCamera.viewport.toGlobal(
    renderWidth,
    renderHeight
  );

  const coordinates = BABYLON.Vector3.Project(
    hotspotPlane.getAbsolutePosition(),
    BABYLON.Matrix.IdentityReadOnly,
    sceneTransformMatrix,
    viewport
  );

  const right = Math.round(renderWidth - coordinates.x).toString() + "px";
  const bottom = Math.round(renderHeight - coordinates.y).toString() + "px";

  const { width, height } = calculatePixel(
    hotspotPlane,
    sceneTransformMatrix,
    viewport
  );

  return { right, bottom, width, height };
}

function getPositionMesh(meshName) {
  let mesh = meshName;
  if (typeof meshName === "string") {
    mesh = window.scene.getMeshByName(meshName);
  }

  let renderWidth = engine.getRenderWidth();
  let renderHeight = engine.getRenderHeight();

  renderWidth *= engine.getHardwareScalingLevel();
  renderHeight *= engine.getHardwareScalingLevel();

  const sceneTransformMatrix = window.scene.getTransformMatrix();

  const viewport = window.scene.activeCamera.viewport.toGlobal(
    renderWidth,
    renderHeight
  );

  const coordinates = BABYLON.Vector3.Project(
    mesh.getAbsolutePosition(),
    BABYLON.Matrix.IdentityReadOnly,
    sceneTransformMatrix,
    viewport
  );

  const right = Math.round(renderWidth - coordinates.x).toString() + "px";
  const bottom = Math.round(renderHeight - coordinates.y).toString() + "px";

  const { width } = calculatePixel(mesh, sceneTransformMatrix, viewport);

  return { right, bottom, width };
}

function calculatePixel(meshName) {
  let mesh = meshName;
  if (typeof meshName === "string") {
    mesh = window.scene.getMeshByName(meshName);
  }

  //note: "engine" is your babylonjs engine variable
  //note: "camera" is the camera variable, optionally you can pass the camera as function parameter (change this function to accept second parameter)
  const temp = new BABYLON.Vector3();
  const vertices = mesh.getBoundingInfo().boundingBox.vectorsWorld;

  let renderWidth = engine.getRenderWidth();
  let renderHeight = engine.getRenderHeight();

  renderWidth *= engine.getHardwareScalingLevel();
  renderHeight *= engine.getHardwareScalingLevel();

  const sceneTransformMatrix = window.scene.getTransformMatrix();

  const viewport = window.scene.activeCamera.viewport.toGlobal(
    renderWidth,
    renderHeight
  );

  let minX = 1e10,
    minY = 1e10,
    maxX = -1e10,
    maxY = -1e10;

  for (const vertex of vertices) {
    BABYLON.Vector3.ProjectToRef(
      vertex,
      BABYLON.Matrix.IdentityReadOnly,
      sceneTransformMatrix,
      viewport,
      temp
    );
    if (minX > temp.x) minX = temp.x;
    if (maxX < temp.x) maxX = temp.x;
    if (minY > temp.y) minY = temp.y;
    if (maxY < temp.y) maxY = temp.y;
  }

  const height = maxX - minX;
  const width = maxY - minY;

  return { height, width, minX, minY, maxX, maxY };
}

function loadHelper() {
  const uniqueID = generateUniqueID();
  pendingOperations.push(uniqueID);

  return new Promise((resolve) => {
    BABYLON.SceneLoader.ImportMesh(
      "",
      `${BASE_URL}avatars/Host/`,
      "Host.gltf",
      window.scene,
      (meshes) => {
        meshes.forEach((mesh) => (mesh.renderingGroupId = 1));

        host = meshes[0];
        host.name = "__root__host";
        host.id = "__root__host";

        const hostPosition = hostPositions[lab];

        host.position = new BABYLON.Vector3(
          hostPosition[0],
          hostPosition[1],
          hostPosition[2]
        );

        const idleAnimation = window.scene.getAnimationGroupByName("Host_Idle");

        idleAnimation.play(true);

        const boxHost = BABYLON.MeshBuilder.CreateBox("boxHost", {
          height: 2,
          width: 1,
          depth: 1.2,
        });

        boxHost.renderingGroupId = 1;
        boxHost.position = new BABYLON.Vector3(
          hostPosition[0],
          1,
          hostPosition[2]
        );
        // boxHost.position = new BABYLON.Vector3(4, 0, 1.7);
        boxHost.rotation = new BABYLON.Vector3(0, -Math.PI / 2, 0);
        boxHost.checkCollisions = true;
        boxHost.isPickable = true;
        boxHost.isVisible = false;

        host.setEnabled(false);

        pendingOperations = pendingOperations.filter((id) => id !== uniqueID);

        resolve();
      }
    );
  });
}

function displayHost(display) {
  host.setEnabled(display);
}

function displayPlayer(thirdPerson) {
  callbackChangePerspective(thirdPerson);
}

function changePerspective(thirdPerson) {
  player.setEnabled(thirdPerson);
}

function loadAvatar(playerName) {
  const uniqueID = generateUniqueID();
  pendingOperations.push(uniqueID);

  playerNameGlobal = playerName;

  displayHost(true);

  return new Promise((resolve) => {
    BABYLON.SceneLoader.ImportMesh(
      "",
      `${BASE_URL}avatars/${playerName}/`,
      `${playerName}.gltf`,
      window.scene,
      (meshes, particleSystems, skeletons) => {
        meshes.forEach((mesh) => (mesh.renderingGroupId = 1));

        player = meshes[0];
        player.name = "__root__player";
        player.id = "__root__player";
        let skeleton = skeletons[0];
        player.skeleton = skeleton;

        const playerPosition = playerPositions[lab];

        if (playerPosition) {
          player.position = new BABYLON.Vector3(
            playerPosition[0],
            playerPosition[1],
            playerPosition[2]
          );
        } else {
          console.error("Add position for player on this lab");
        }

        player.checkCollisions = true;
        player.ellipsoid = new BABYLON.Vector3(0.5, 1, 0.5);
        player.ellipsoidOffset = new BABYLON.Vector3(0, 1, 0);
        player.rotation = playerRotation;

        const walkingAnimation = window.scene.getAnimationGroupByName(
          `${playerName}_Walking`
        );

        const idleAnimation = window.scene.getAnimationGroupByName(
          `${playerName}_Idle`
        );

        const agMap = {
          idle: idleAnimation,
          walk: walkingAnimation,
          run: walkingAnimation,
          walkBack: walkingAnimation,
          walkBackFast: walkingAnimation,
          turnRight: idleAnimation,
          turnRightFast: idleAnimation,
          turnLeft: idleAnimation,
          turnLeftFast: idleAnimation,
          strafeRight: walkingAnimation,
          strafeRightFast: walkingAnimation,
          strafeLeft: walkingAnimation,
          strafeLeftFast: walkingAnimation,
        };

        cc = new CharacterController(
          player,
          window.scene.getCameraByName("Main"),
          window.scene,
          agMap
        ); // , agMap
        //below makes the controller point the camera at the player head which is approx
        //1.5m above the player origin
        cc.setCameraTarget(new BABYLON.Vector3(0, 1.5, 0));

        //if the camera comes close to the player we want to enter first person mode.
        cc.setNoFirstPerson(false);

        //the height of steps which the player can climb
        cc.setStepOffset(1);
        //the minimum and maximum slope the player can go up
        //between the two the player will start sliding down if it stops
        cc.setSlopeLimit(30, 60);

        //tell controller
        // - which animation range should be used for which player animation
        // - rate at which to play that animation range
        // - wether the animation range should be looped

        cc.setIdleAnim(idleAnimation, 1, true);

        cc.setWalkAnim(walkingAnimation, 1, true);
        cc.setRunAnim(walkingAnimation, 1, true);

        cc.setWalkBackAnim(walkingAnimation, 1, true);
        cc.setWalkBackFastAnim(walkingAnimation, 1, true);

        cc.setTurnRightAnim(idleAnimation, 1, false);
        cc.setTurnRightFastAnim(idleAnimation, 1, false);

        cc.setTurnLeftAnim(idleAnimation, 1, false);
        cc.setTurnLeftFastAnim(idleAnimation, 1, false);

        cc.setStrafeRightAnim(walkingAnimation, 1, true);
        cc.setStrafeRightFastAnim(walkingAnimation, 1, true);

        cc.setStrafeLeftAnim(walkingAnimation, 1, true);
        cc.setStrafeLeftFastAnim(walkingAnimation, 1, true);

        // Disable jump key to avoid the usage of this interaction
        cc.setJumpKey("");
        // Set run and walk speed with the same value to fake not running
        cc.setRunSpeed(3.5);
        cc.setWalkSpeed(3.5);
        // Set run and walk back speed with the same value to fake not running back
        cc.setBackFastSpeed(2.5);
        cc.setBackSpeed(2.5);
        // Increase turn speed for a better 3rd person performance
        cc.setTurnFastSpeed(50);
        cc.setTurnSpeed(50);

        cc.start();

        pendingOperations = pendingOperations.filter((id) => id !== uniqueID);

        lookingAt = "host";

        resolve();
      }
    );
  });
}

let skipLoad = false;

function setSkipLoad(value) {
  skipLoad = value;
  for (let index = 0; index < slideInAnimations.length; index++) {
    slideInAnimations[index].animation.stop();
  }
  for (let index = 0; index < doorAnimations.length; index++) {
    doorAnimations[index].animation.stop();
  }
  slideInAnimations = [];
  doorAnimations = [];
}

async function loadProduct(productObj, force = false, replace = false) {
  cc.enableKeyBoard(false);
  setCamera("Helper");

  product = window.scene.getTransformNodeByName(`${productObj.model}_Group`);

  lookingAt = "rails";

  const boxProduct = window.scene.getMeshByName(
    `boxProduct_${productObj.model}`
  );

  const boxPipes = window.scene.getMeshByName(`boxPipes_${productObj.model}`);
  const boxHeatExchanger = window.scene.getMeshByName(
    `boxHeatExchanger_${productObj.model}`
  );

  boxProduct.setEnabled(true);

  if (boxPipes) {
    boxPipes.setEnabled(true);
  }

  if (boxHeatExchanger) {
    boxHeatExchanger.setEnabled(true);
  }

  product.setEnabled(true);

  if (!force) {
    const rails = window.scene.getMeshByName("P_Rails");
    const boxRails = window.scene.getMeshByName("boxRails");

    const finalRails = initialPositions["RAILS"];
    const finalProduct = initialPositions[productObj.model];

    boxRails.setEnabled(true);
    if (!replace) {
      // If we are replacing product this was already sounding
      if (!skipLoad) {
        playLoopSound("rails", "rail.mp3");
      } else {
        return await finishLoadProduct(
          rails,
          product,
          finalRails,
          finalProduct,
          boxRails,
          productObj
        );
      }

      // If we were replacing product the doors and rails are already set
      if (!skipLoad) {
        openDoors(true, 0.5);
      } else {
        return await finishLoadProduct(
          rails,
          product,
          finalRails,
          finalProduct,
          boxRails,
          productObj
        );
      }

      if (lab === "Chill_Lab") {
        if (!skipLoad) {
          await slideInAnimation(rails, "RAILS", false, true);
        } else {
          return await finishLoadProduct(
            rails,
            product,
            finalRails,
            finalProduct,
            boxRails,
            productObj
          );
        }
      }
    }

    if (lab === "Chill_Lab") {
      if (!skipLoad) {
        slideInAnimation(rails, "RAILS", true, true);
      } else {
        return await finishLoadProduct(
          rails,
          product,
          finalRails,
          finalProduct,
          boxRails,
          productObj
        );
      }
    }

    if (!skipLoad) {
      await slideInAnimation(product, productObj.model, true, true);
    } else {
      return await finishLoadProduct(
        rails,
        product,
        finalRails,
        finalProduct,
        boxRails,
        productObj
      );
    }

    return await finishLoadProduct(
      rails,
      product,
      finalRails,
      finalProduct,
      boxRails,
      productObj
    );
  } else {
    if (lab === "Chill_Lab") {
      product.position.x += 15;
      await loadCables(true, productObj, force);
    } else {
      product.position.z -= 15;
      await loadDuct(true, productObj, force);
    }
  }
}

async function finishLoadProduct(
  rails,
  product,
  finalRails,
  finalProduct,
  boxRails,
  productObj
) {
  stopSound();
  boxRails.setEnabled(false);

  if (lab === "Chill_Lab") {
    rails.position.x = finalRails;
    product.position.x = finalProduct;
    openDoors(false);
    await loadCables(true, productObj, skipLoad);
  } else {
    product.position.z = finalProduct;
    openDoors(false);
    await loadDuct(true, productObj, skipLoad);
  }
}

async function hideProduct(productObj, force = false, replace = false) {
  const boxPipes = window.scene.getMeshByName(`boxPipes_${productObj.model}`);
  const boxHeatExchanger = window.scene.getMeshByName(
    `boxHeatExchanger_${productObj.model}`
  );

  if (boxPipes) {
    boxPipes.setEnabled(false);
  }

  if (boxHeatExchanger) {
    boxHeatExchanger.setEnabled(false);
  }

  const product = window.scene.getTransformNodeByName(
    `${productObj.model}_Group`
  );

  if (!force) {
    cc.enableKeyBoard(false);
    setCamera("Helper");
    lookingAt = "rails";
  }

  if (lab === "Chill_Lab") {
    await loadCables(false, productObj, force);
  } else {
    await loadDuct(false, productObj, force);
  }

  const boxProduct = window.scene.getMeshByName(
    `boxProduct_${productObj.model}`
  );

  if (!force) {
    const boxRails = window.scene.getMeshByName("boxRails");

    boxRails.setEnabled(true);
    const rails = window.scene.getMeshByName("P_Rails");

    const finalRails = !replace
      ? initialPositions["RAILS"]
      : initialPositions["RAILS"] - (lab === "Chill_Lab" ? 1 : -1) * 15;
    const finalProduct =
      initialPositions[productObj.model] - (lab === "Chill_Lab" ? 1 : -1) * 15;

    if (!skipLoad) {
      playLoopSound("rails", "rail.mp3");
    } else {
      return await finishHideProduct(
        rails,
        finalRails,
        finalProduct,
        boxRails,
        boxProduct,
        product,
        replace
      );
    }

    if (!skipLoad) {
      openDoors(true, 0.5);
    } else {
      return await finishHideProduct(
        rails,
        finalRails,
        finalProduct,
        boxRails,
        boxProduct,
        product,
        replace
      );
    }

    if (lab === "Chill_Lab") {
      if (!skipLoad) {
        slideInAnimation(rails, "RAILS", false, true);
      } else {
        return await finishHideProduct(
          rails,
          finalRails,
          finalProduct,
          boxRails,
          boxProduct,
          product,
          replace
        );
      }
    }

    if (!skipLoad) {
      await slideInAnimation(product, productObj.model, false, true);
    } else {
      return await finishHideProduct(
        rails,
        finalRails,
        finalProduct,
        boxRails,
        boxProduct,
        product,
        replace
      );
    }

    if (!replace) {
      // If we are not going to insert now the other product
      // Return rails to their position
      if (lab === "Chill_Lab") {
        if (!skipLoad) {
          await slideInAnimation(rails, "RAILS", true, true);
        } else {
          return await finishHideProduct(
            rails,
            finalRails,
            finalProduct,
            boxRails,
            boxProduct,
            product,
            replace
          );
        }
      }
    }

    return await finishHideProduct(
      rails,
      finalRails,
      finalProduct,
      boxRails,
      boxProduct,
      product,
      replace
    );
  } else {
    if (lab === "Chill_Lab") {
      product.position.x -= 15;
    } else {
      product.position.x += 15;
    }

    if (!replace) {
      boxProduct.setEnabled(false);

      cc.enableKeyBoard(true);
      setCamera("Main");
      lookingAt = "host";
    }
    product.setEnabled(false);
  }
}

async function finishHideProduct(
  rails,
  finalRails,
  finalProduct,
  boxRails,
  boxProduct,
  product,
  replace
) {
  stopSound();
  boxRails.setEnabled(false);
  boxProduct.setEnabled(false);

  if (!replace) {
    cc.enableKeyBoard(true);
    setCamera("Main");
    lookingAt = "host";

    openDoors(false);
  }
  product.setEnabled(false);

  if (lab === "Chill_Lab") {
    rails.position.x = finalRails;
    product.position.x = finalProduct;
  } else {
    product.position.z = finalProduct;
  }
}

async function setLegsOnGround({ load, productObj, force = false }) {
  const legs = window.scene.getMeshByName(`P_${productObj.model}_Legs`);

  let to;
  switch (productObj.model) {
    case "CWA":
      to = load ? 0.75 : 0.88;
      break;
    case "PDX":
      to = load ? 0.455 : 0.563;
      break;
  }

  if (!force) {
    await dropDownAnimation({ mesh: legs, to, speed: 0.4 });
    if (load) {
      await highlight(
        `P_${productObj.model}_Platform`,
        "electric",
        true,
        false
      );
    }
  } else {
    legs.position.y = to;
  }
}

async function loadDuct(load, productObj, force = false) {
  await setLegsOnGround({ load, productObj, force });

  const duct = window.scene.getMeshByName(`P_${productObj.model}_Duct`);

  if (load) {
    duct.setEnabled(true);
  }

  if (!force) {
    await showAnimation(duct, load, 0.4);
  } else {
    duct.visibility = load ? 1 : 0;
  }

  if (!load) {
    duct.setEnabled(false);
  }

  await loadCables(load, productObj, force || skipLoad);
}

async function loadCables(load, productObj, force = false) {
  let int;
  let ext;
  let cover;

  if (lab === "Chill_Lab") {
    int = window.scene.getMeshByName("P_Cables_PowerFlow_Interior");
    ext = window.scene.getMeshByName("P_Cables_PowerFlow_Exterior");
  } else {
    int = window.scene.getMeshByName(
      `P_${productObj.model}_Cables_PowerFlow_Interior`
    );
    ext = window.scene.getMeshByName(
      `P_${productObj.model}_Cables_PowerFlow_Exterior`
    );
    cover = window.scene.getMeshByName(`P_${productObj.model}_Cables_Cover`);
  }

  if (load) {
    createPowerAnimation(int);
    ext.setEnabled(true);
    if (lab === "Indoor_Lab") {
      cover.setEnabled(true);
    }
  }

  if (!force) {
    if (!load) {
      if (lab === "Indoor_Lab") {
        await showAnimation(cover, false, 0.4);
      }
    }
    await showAnimation(ext, load, 0.4);
    if (load) {
      if (lab === "Indoor_Lab") {
        await showAnimation(cover, true, 0.4);
      }
    }
  } else {
    ext.visibility = load ? 1 : 0;
    if (lab === "Indoor_Lab") {
      cover.visibility = load ? 1 : 0;
    }
  }

  if (!load) {
    ext.setEnabled(false);
    if (lab === "Indoor_Lab") {
      cover.setEnabled(false);
    }
  }

  await loadHoses(load, productObj, force || skipLoad);
}

async function loadHoses(load, productObj, force = false) {
  const extSup = window.scene.getMeshByName(
    `P_${productObj.model}_WaterFlow_ExteriorSuperior`
  );
  const extInf = window.scene.getMeshByName(
    `P_${productObj.model}_WaterFlow_ExteriorInferior`
  );
  let heatExchanger;

  let outlet;
  let inlet;

  if (lab === "Chill_Lab") {
    outlet = window.scene.getMeshByName(
      `P_${productObj.model}_Outlet_Connection`
    );
    inlet = window.scene.getMeshByName(
      `P_${productObj.model}_Inlet_Connection`
    );
  } else if (productObj.model === "PDX") {
    heatExchanger = window.scene.getMeshByName(`P_PDX_Heat_Exchanger`);
  }

  if (load) {
    createWaterAnimation(productObj);
    extSup.setEnabled(true);
    extInf.setEnabled(true);

    if (lab === "Chill_Lab") {
      outlet.setEnabled(true);
      inlet.setEnabled(true);
    } else if (productObj.model === "PDX") {
      heatExchanger.setEnabled(true);
    }
  }

  if (!force) {
    const promises = [
      showAnimation(extSup, load, 1),
      showAnimation(extInf, load, 1),
    ];

    if (lab === "Chill_Lab") {
      promises.push(
        showAnimation(outlet, load, 1),
        showAnimation(inlet, load, 1)
      );
    } else if (productObj.model === "PDX") {
      promises.push(showAnimation(heatExchanger, load, 1));
    }

    await Promise.all(promises);
  } else {
    extSup.visibility = load ? 1 : 0;
    extInf.visibility = load ? 1 : 0;
    if (lab === "Chill_Lab") {
      outlet.visibility = load ? 1 : 0;
      inlet.visibility = load ? 1 : 0;
    } else if (productObj.model === "PDX") {
      heatExchanger.visibility = load ? 1 : 0;
    }
  }

  if (!load) {
    extSup.setEnabled(false);
    extInf.setEnabled(false);
    if (lab === "Chill_Lab") {
      outlet.setEnabled(false);
      inlet.setEnabled(false);
    } else if (productObj.model === "PDX") {
      heatExchanger.setEnabled(false);
    }
  }
}

async function loadWaterDrop(load, force) {
  const mesh = window.scene.getMeshByName("P_AFC_AD_Drop_Water");

  if (load) {
    mesh.setEnabled(true);
  }

  if (!force) {
    await Promise.all([showAnimation(mesh, load, 1)]);
  } else {
    mesh.visibility = load ? 1 : 0;
  }

  if (!load) {
    mesh.setEnabled(false);
  }
}

async function loadAirFlow(load, force = false, productObj) {
  let meshHot;
  let meshCold;
  let meshExterior;
  let meshPlatform;
  let meshDuctHorizontal;
  // let meshDuctVertical;

  if (lab === "Chill_Lab") {
    meshHot = window.scene.getMeshByName("P_AirFlow_Hot");
    meshCold = window.scene.getMeshByName("P_AirFlow_Cold");
  } else if (lab === "Indoor_Lab") {
    meshHot = window.scene.getMeshByName(`P_${productObj.model}_AirFlow_Hot`);
    meshCold = window.scene.getMeshByName(`P_${productObj.model}_AirFlow_Cold`);
    meshExterior = window.scene.getMeshByName(`P_${productObj.model}_Exterior`);
    if (productObj.model === "PDX") {
      meshPlatform = window.scene.getMeshByName(`P_PDX_Platform`);
    }
    meshDuctHorizontal = window.scene.getMeshByName(
      `P_${productObj.model}_Duct`
    );
    // meshDuctVertical = window.scene.getMeshByName(`P_AirFlow_Connection`);
  }

  if (load) {
    meshCold.setEnabled(true);
    meshHot.setEnabled(true);
  }

  if (!force) {
    const promises = [
      showAnimation(meshCold, load, 0.4),
      showAnimation(meshHot, load, 0.4),
    ];

    if (lab === "Indoor_Lab") {
      promises.push(
        showAnimation(meshExterior, !load, 1, 0.33),
        showAnimation(meshDuctHorizontal, !load, 1, 0.33)
        // showAnimation(meshDuctVertical, !load, 0.4, 0.33)
      );
      if (productObj.model === "PDX") {
        promises.push(showAnimation(meshPlatform, !load, 1, 0.33));
      }
    }

    await Promise.all(promises);
  } else {
    meshCold.visibility = load ? 1 : 0;
    meshHot.visibility = load ? 1 : 0;

    if (lab === "Indoor_Lab") {
      if (meshExterior) {
        meshExterior.visibility = load ? 0.33 : 1;
      }

      if (meshPlatform) {
        meshPlatform.visibility = load ? 0.33 : 1;
      }

      if (meshDuctHorizontal) {
        meshDuctHorizontal.visibility = load ? 0.33 : 1;
      }

      // if (meshDuctVertical) {
      //   meshDuctVertical.visibility = load ? 0.33 : 1;
      // }
    }
  }

  if (!load) {
    meshCold.setEnabled(false);
    meshHot.setEnabled(false);
  }
}

async function loadControlRoom(load) {
  const meshGlassClear = window.scene.getMeshByName(
    "P_Control_Room_Window_Clear"
  );
  const meshGlassDark = window.scene.getMeshByName(
    "P_Control_Room_Window_Dark"
  );

  if (load) {
    meshGlassClear.setEnabled(true);
  } else {
    meshGlassDark.setEnabled(true);
  }

  await Promise.all([
    showAnimation(meshGlassClear, load, 0.4),
    showAnimation(meshGlassDark, !load, 0.4),
  ]);

  if (load) {
    meshGlassDark.setEnabled(false);
  } else {
    meshGlassClear.setEnabled(false);
  }
}

async function loadCompressor({ load, productObj, force = false }) {
  // P_AFC_NA_Compressor
  const ext = window.scene.getMeshByName(`P_${productObj.model}`);

  if (!force) {
    await showAnimation(ext, !load, 0.4, 0.33);
  } else {
    ext.visibility = load ? 0.33 : 1;
  }
}

function showAnimation(mesh, show, speed = 0.1, visibility = 0) {
  if (process.env.NODE_ENV === "development") {
    speed *= window.speedFactor || 1;
  }

  if (!mesh) {
    console.error("Mesh non existing");
    return;
  } else if (typeof mesh === "string") {
    mesh = window.scene.getMeshByName(mesh);
  }

  const keysFade = [
    {
      frame: 0,
      value: mesh.visibility,
    },
    {
      frame: 1,
      value: show ? 1 : visibility,
    },
  ];

  const fadeAnimation = new BABYLON.Animation(
    "fade",
    "visibility",
    1,
    BABYLON.Animation.ANIMATIONTYPE_FLOAT,
    BABYLON.Animation.ANIMATIONLOOPMODE_CONSTANT,
    true
  );

  fadeAnimation.setKeys(keysFade);

  return new Promise((resolve) => {
    window.scene.beginDirectAnimation(
      mesh,
      [fadeAnimation],
      0,
      1,
      false,
      speed,
      () => {
        mesh.visibility = show ? 1 : visibility;
        resolve();
      },
      null,
      true
    );
  });
}

function colorAnimation({ mesh, color, speed = 0.1 }) {
  if (process.env.NODE_ENV === "development") {
    speed *= window.speedFactor || 1;
  }

  if (typeof mesh === "string") {
    mesh = window.scene.getMeshByName(mesh);
  }

  const keys = [
    {
      frame: 0,
      value: mesh.material.albedoColor,
    },
    {
      frame: 1,
      value: BABYLON.Color3.FromHexString(color).toLinearSpace(),
    },
  ];

  const animation = new BABYLON.Animation(
    "color",
    "material.albedoColor",
    1,
    BABYLON.Animation.ANIMATIONTYPE_COLOR3,
    BABYLON.Animation.ANIMATIONLOOPMODE_CONSTANT
  );

  animation.setKeys(keys);

  return new Promise((resolve) => {
    window.scene.beginDirectAnimation(
      mesh,
      [animation],
      0,
      1,
      false,
      speed,
      resolve
    );
  });
}

function dropDownAnimation({ mesh, to, speed = 0.1 }) {
  if (process.env.NODE_ENV === "development") {
    speed *= window.speedFactor || 1;
  }

  if (typeof mesh === "string") {
    mesh = window.scene.getMeshByName(mesh);
  }

  const keysdropDown = [
    {
      frame: 0,
      value: mesh.position.y,
    },
    {
      frame: 1,
      value: to,
    },
  ];

  const ddAnimation = new BABYLON.Animation(
    "dropDown",
    "position.y",
    1,
    BABYLON.Animation.ANIMATIONTYPE_FLOAT,
    BABYLON.Animation.ANIMATIONLOOPMODE_CONSTANT
  );

  ddAnimation.setKeys(keysdropDown);

  return new Promise((resolve) => {
    window.scene.beginDirectAnimation(
      mesh,
      [ddAnimation],
      0,
      1,
      false,
      speed,
      resolve
    );
  });
}

let slideInAnimations = [];
async function slideInAnimation(
  mesh,
  meshName,
  show,
  useSkip = false,
  speed = 0.1
) {
  if (process.env.NODE_ENV === "development") {
    speed *= window.speedFactor || 1;
  }

  if (typeof mesh === "string") {
    mesh = window.scene.getMeshByName(mesh);
  }

  const initial = lab === "Chill_Lab" ? mesh.position.x : mesh.position.z;

  // initialPositions[meshName]

  const final = show
    ? initialPositions[meshName]
    : initialPositions[meshName] - (lab === "Chill_Lab" ? 1 : -1) * 15;

  const keysdropDown = [
    {
      frame: 0,
      value: initial,
    },
    {
      frame: 1,
      value: final,
    },
  ];

  const ddAnimation = new BABYLON.Animation(
    "dropDown",
    lab === "Chill_Lab" ? "position.x" : "position.z",
    1,
    BABYLON.Animation.ANIMATIONTYPE_FLOAT,
    BABYLON.Animation.ANIMATIONLOOPMODE_CONSTANT
  );

  ddAnimation.setKeys(keysdropDown);

  await new Promise((resolve) => {
    const uniqueID = generateUniqueID();
    const animation = window.scene.beginDirectAnimation(
      mesh,
      [ddAnimation],
      0,
      1,
      false,
      speed,
      () => {
        if (!skipLoad && useSkip) {
          slideInAnimations = slideInAnimations.filter(
            (el) => el.uniqueID !== uniqueID
          );
        }

        resolve();
      }
    );

    if (useSkip) {
      slideInAnimations.push({ uniqueID, animation });
    }
  });
}

let initialPanelPosition;
let finalPanelPosition;

function soundPanelAnimation(show, speed = 0.1) {
  if (process.env.NODE_ENV === "development") {
    speed *= window.speedFactor || 1;
  }

  const AFC_SoundPanel = window.scene.getTransformNodeByName("AFC_SoundPanel");

  const initialY = AFC_SoundPanel.position.y;
  const keysdropDown = [
    {
      frame: 0,
      value: initialY,
    },
    {
      frame: 1,
      value: show ? finalPanelPosition : initialPanelPosition,
    },
  ];

  const ddAnimation = new BABYLON.Animation(
    "dropDown",
    "position.y",
    1,
    BABYLON.Animation.ANIMATIONTYPE_FLOAT,
    BABYLON.Animation.ANIMATIONLOOPMODE_CONSTANT
  );

  ddAnimation.setKeys(keysdropDown);

  return new Promise((resolve) => {
    window.scene.beginDirectAnimation(
      AFC_SoundPanel,
      [ddAnimation],
      0,
      1,
      false,
      speed,
      () => {
        resolve();
      }
    );
  });
}

function lightAnimation(show, speed = 1) {
  if (process.env.NODE_ENV === "development") {
    speed *= window.speedFactor || 1;
  }

  const easingFunction = new BABYLON.BezierCurveEase(0.32, 0, 0.67, 0);

  const light1 = window.scene.getLightByName("light1");
  const light2 = window.scene.getLightByName("light2");

  const keysLight = [
    {
      frame: 0,
      value: 0,
    },
    {
      frame: 1,
      value: 0.7,
    },
  ];

  const lightAnimation = new BABYLON.Animation(
    "light",
    "intensity",
    1,
    BABYLON.Animation.ANIMATIONTYPE_FLOAT,
    BABYLON.Animation.ANIMATIONLOOPMODE_CONSTANT
  );

  lightAnimation.setKeys(keysLight);

  lightAnimation.setEasingFunction(easingFunction);

  const promises = [];
  if (light1) {
    const prAnimationLight1 = new Promise((resolve) => {
      window.scene.beginDirectAnimation(
        light1,
        [lightAnimation],
        show ? 0 : 1,
        show ? 1 : 0,
        false,
        speed,
        resolve
      );
    });

    promises.push(prAnimationLight1);
  }

  if (light2) {
    const prAnimationLight2 = new Promise((resolve) => {
      window.scene.beginDirectAnimation(
        light2,
        [lightAnimation],
        show ? 0 : 1,
        show ? 1 : 0,
        false,
        speed,
        resolve
      );
    });

    promises.push(prAnimationLight2);
  }

  const keysNormal = [
    {
      frame: 0,
      value: 0,
    },
    {
      frame: 1,
      value: 1,
    },
  ];

  const envAnimation = new BABYLON.Animation(
    "env",
    "environmentIntensity",
    1,
    BABYLON.Animation.ANIMATIONTYPE_FLOAT,
    BABYLON.Animation.ANIMATIONLOOPMODE_CONSTANT
  );

  envAnimation.setKeys(keysNormal);
  envAnimation.setEasingFunction(easingFunction);

  const prEnvAnimation = new Promise((resolve) => {
    window.scene.beginDirectAnimation(
      window.scene,
      [envAnimation],
      show ? 0 : 1,
      show ? 1 : 0,
      false,
      speed,
      resolve
    );
  });

  promises.push(prEnvAnimation);

  const lightCeiling = window.scene.getMaterialByName("M_Ceiling");

  if (lightCeiling) {
    try {
      // TODO
      const lightCeilingAnimation = new BABYLON.Animation(
        "lightCeilingAnimation",
        "emissiveIntensity",
        1,
        BABYLON.Animation.ANIMATIONTYPE_FLOAT,
        BABYLON.Animation.ANIMATIONLOOPMODE_CONSTANT
      );

      lightCeilingAnimation.setKeys(keysNormal);
      lightCeilingAnimation.setEasingFunction(easingFunction);

      const prLightCeilingAnimation = new Promise((resolve) => {
        window.scene.beginDirectAnimation(
          lightCeiling,
          [lightCeilingAnimation],
          show ? 0 : 1,
          show ? 1 : 0,
          false,
          speed,
          resolve
        );
      });

      promises.push(prLightCeilingAnimation);
      // lightCeiling.emissiveIntensity = show ? 1 : 0;
    } catch (error) {
      console.log(error);
    }
  }

  const lightCeilingCRAC = window.scene.getMaterialByName("M_Structure");

  if (lightCeilingCRAC) {
    try {
      // TODO
      const lightCeilingCRACAnimation = new BABYLON.Animation(
        "lightCeilingCRACAnimation",
        "emissiveIntensity",
        1,
        BABYLON.Animation.ANIMATIONTYPE_FLOAT,
        BABYLON.Animation.ANIMATIONLOOPMODE_CONSTANT
      );

      lightCeilingCRACAnimation.setKeys(keysNormal);
      lightCeilingCRACAnimation.setEasingFunction(easingFunction);

      const prLightCeilingCRACAnimation = new Promise((resolve) => {
        window.scene.beginDirectAnimation(
          lightCeilingCRAC,
          [lightCeilingCRACAnimation],
          show ? 0 : 1,
          show ? 1 : 0,
          false,
          speed,
          resolve
        );
      });

      promises.push(prLightCeilingCRACAnimation);
      // lightCeilingCRAC.emissiveIntensity = show ? 1 : 0;
    } catch (error) {
      console.log(error);
    }
  }

  const lightProps = window.scene.getMaterialByName("M_Props");

  if (lightProps) {
    try {
      const lightPropsAnimation = new BABYLON.Animation(
        "lightPropsAnimation",
        "emissiveIntensity",
        1,
        BABYLON.Animation.ANIMATIONTYPE_FLOAT,
        BABYLON.Animation.ANIMATIONLOOPMODE_CONSTANT
      );

      lightPropsAnimation.setKeys(keysNormal);
      lightPropsAnimation.setEasingFunction(easingFunction);

      const prLightPropsAnimation = new Promise((resolve) => {
        window.scene.beginDirectAnimation(
          lightProps,
          [lightPropsAnimation],
          show ? 0 : 1,
          show ? 1 : 0,
          false,
          speed,
          resolve
        );
      });

      promises.push(prLightPropsAnimation);
      // lightProps.emissiveIntensity = show ? 1 : 0;
    } catch (error) {
      console.log(error);
    }
  }

  return Promise.all(promises);
}

window.lightAnimation = lightAnimation;

function movePlayer(final, speed = 1) {
  player.lookAt(final);

  const initial = new BABYLON.Vector3(
    player.position.x,
    player.position.y,
    player.position.z
  );

  const keys = [
    {
      frame: 0,
      value: initial,
    },
    {
      frame: 1,
      value: final,
    },
  ];

  const animation = new BABYLON.Animation(
    "movePlayer",
    "position",
    1,
    BABYLON.Animation.ANIMATIONTYPE_VECTOR3,
    BABYLON.Animation.ANIMATIONLOOPMODE_CONSTANT
  );

  animation.setKeys(keys);

  return new Promise((resolve) => {
    window.scene.beginDirectAnimation(
      player,
      [animation],
      0,
      1,
      false,
      speed,
      () => {
        resolve();
      }
    );
  });
}

async function securePlayer(force = false, mode = "product", speed = 4) {
  if (process.env.NODE_ENV === "development") {
    speed *= window.speedFactor || 1;
  }

  const securePlayerPoints = securePlayerPointss[lab];

  if (!force) {
    cc.enableKeyBoard(false);

    setCamera("Helper");

    cc.pauseAnim();
    const walkingAnimation = window.scene.getAnimationGroupByName(
      `${playerNameGlobal}_Walking`
    );

    walkingAnimation.play(true);

    const initial = new BABYLON.Vector3(
      player.position.x,
      player.position.y,
      player.position.z
    );

    // bottom left
    const starting1 = new BABYLON.Vector3(
      securePlayerPoints.starting1[0],
      player.position.y,
      securePlayerPoints.starting1[1]
    );
    const distance1 = BABYLON.Vector3.Distance(starting1, initial);

    // top left
    const starting2 = new BABYLON.Vector3(
      securePlayerPoints.starting2[0],
      player.position.y,
      securePlayerPoints.starting2[1]
    );
    const distance2 = BABYLON.Vector3.Distance(starting2, initial);

    // top right
    const starting3 = new BABYLON.Vector3(
      securePlayerPoints.starting3[0],
      player.position.y,
      securePlayerPoints.starting3[1]
    );
    const distance3 = BABYLON.Vector3.Distance(starting3, initial);

    // bottom right
    const starting4 = new BABYLON.Vector3(
      securePlayerPoints.starting4[0],
      player.position.y,
      securePlayerPoints.starting4[1]
    );
    const distance4 = BABYLON.Vector3.Distance(starting4, initial);

    const distance = Math.min(distance1, distance2, distance3, distance4);

    if (distance === distance2) {
      // Or go directly to point 1 or go point 2 then point 1
      if (distance1 > distance3) {
        // Go point 2 then point 1
        if (distance2) {
          await movePlayer(starting2, speed / distance2);
        }

        const distance21 = BABYLON.Vector3.Distance(starting2, starting1);
        await movePlayer(starting1, speed / distance21);
      } else {
        // Do nothing directly point 1
        if (distance1) {
          await movePlayer(starting1, speed / distance1);
        }
      }
    } else if (distance === distance3) {
      // Go directly to point 2/4 then point 1
      if (distance2 > distance4) {
        // Go point 4 then point 1
        if (distance4) {
          await movePlayer(starting4, speed / distance4);
        }

        const distance41 = BABYLON.Vector3.Distance(starting4, starting1);
        await movePlayer(starting1, speed / distance41);
      } else {
        // Go point 2 then point 1
        if (distance2) {
          await movePlayer(starting2, speed / distance2);
        }

        const distance21 = BABYLON.Vector3.Distance(starting2, starting1);
        await movePlayer(starting1, speed / distance21);
      }
    } else if (distance === distance4) {
      // Or go directly to point 1 or go point 4 then 1
      if (distance1 > distance3) {
        // Go point 4 then point 1
        if (distance4) {
          await movePlayer(starting4, speed / distance4);
        }

        const distance41 = BABYLON.Vector3.Distance(starting4, starting1);
        await movePlayer(starting1, speed / distance41);
      } else {
        // Do nothing directly point 1
        if (distance1 !== 0) {
          await movePlayer(starting1, speed / distance1);
        }
      }
    } else if (distance1 !== 0) {
      // if none meet nothing, normal path, closer to point 1
      await movePlayer(starting1, speed / distance1);
    }

    walkingAnimation.stop();
    cc.resumeAnim();

    setCamera("Main");

    cc.enableKeyBoard(true);
  } else {
    let position;
    switch (mode) {
      case "welcome":
        // position = new BABYLON.Vector3(4.8, player.position.y, -4.25);
        if (playerPositions[lab]) {
          position = new BABYLON.Vector3(
            playerPositions[lab][0],
            playerPositions[lab][1],
            playerPositions[lab][2]
          );
        }
        break;
      case "product":
        position = new BABYLON.Vector3(
          securePlayerPoints.starting1[0],
          player.position.y,
          securePlayerPoints.starting1[1]
        );
        break;
      case "power":
        position = new BABYLON.Vector3(7, player.position.y, 2);
        break;
      case "hydraulic":
        position = new BABYLON.Vector3(4, player.position.y, -3);
        break;
      case "thermoregulation":
      case "airDuct":
        position = new BABYLON.Vector3(6, player.position.y, -2);
        break;
    }
    player.position = position;
  }

  let rotation;
  let alphaD;
  let betaD;
  let radiusD;
  switch (mode) {
    case "welcome":
      rotation = 270;
      alphaD = 63;
      betaD = 75;
      radiusD = 3;
      break;
    case "product":
      rotation = rotationPlayerTest[lab].rotation;
      alphaD = rotationPlayerTest[lab].alphaD;
      betaD = rotationPlayerTest[lab].betaD;
      break;
    case "power":
      rotation = 270;
      alphaD = 0;
      break;
    case "hydraulic":
      rotation = 270;
      alphaD = 0;
      break;
    case "thermoregulation":
    case "airDuct":
      rotation = -65;
      alphaD = -35;
      betaD = 90;
      break;
  }
  player.rotation.y = (rotation * Math.PI) / 180;

  const alpha = (alphaD * Math.PI) / 180;
  const beta = betaD ? (betaD * Math.PI) / 180 : 1.37;
  const radius = radiusD ? radiusD : 1.5;

  window.scene.activeCamera.alpha = alpha;
  window.scene.activeCamera.beta = beta;
  window.scene.activeCamera.radius = radius;
}

let doorAnimations = [];

function openDoors(open, speed = 1, angle = 45) {
  if (process.env.NODE_ENV === "development") {
    speed *= window.speedFactor || 1;
  }

  const meshRight = window.scene.getNodeByName("Ani_Door_Right");
  const meshLeft = window.scene.getNodeByName("Ani_Door_Left");

  const initialR = meshRight.rotationQuaternion.y;
  const initialL = meshLeft.rotationQuaternion.y;

  const finalR = open ? (-angle * Math.PI) / 180 : 0;
  const finalL = open ? (angle * Math.PI) / 180 : 0;

  const keysR = [
    {
      frame: 0,
      value: initialR,
    },
    {
      frame: 1,
      value: finalR,
    },
  ];

  const keysL = [
    {
      frame: 0,
      value: initialL,
    },
    {
      frame: 1,
      value: finalL,
    },
  ];

  const animationR = new BABYLON.Animation(
    "openDoorsR",
    "rotationQuaternion.y",
    1,
    BABYLON.Animation.ANIMATIONTYPE_FLOAT,
    BABYLON.Animation.ANIMATIONLOOPMODE_CONSTANT
  );

  animationR.setKeys(keysR);

  const animationL = new BABYLON.Animation(
    "openDoorsL",
    "rotationQuaternion.y",
    1,
    BABYLON.Animation.ANIMATIONTYPE_FLOAT,
    BABYLON.Animation.ANIMATIONLOOPMODE_CONSTANT
  );

  animationL.setKeys(keysL);

  const promises = [];

  promises.push(
    new Promise((resolve) => {
      const uniqueIDR = generateUniqueID();

      const animation = window.scene.beginDirectAnimation(
        meshRight,
        [animationR],
        0,
        1,
        false,
        speed,
        () => {
          if (!skipLoad) {
            doorAnimations = doorAnimations.filter(
              (el) => el.uniqueID !== uniqueIDR
            );
          }

          resolve();
        }
      );

      if (!skipLoad) {
        doorAnimations.push({ uniqueID: uniqueIDR, animation });
      }
    })
  );

  promises.push(
    new Promise((resolve) => {
      const uniqueIDL = generateUniqueID();

      const animation = window.scene.beginDirectAnimation(
        meshLeft,
        [animationL],
        0,
        1,
        false,
        speed,
        () => {
          if (!skipLoad) {
            doorAnimations = doorAnimations.filter(
              (el) => el.uniqueID !== uniqueIDL
            );
          }

          resolve();
        }
      );

      if (!skipLoad) {
        doorAnimations.push({ uniqueID: uniqueIDL, animation });
      }
    })
  );

  return promises;
}

window.openDoors = openDoors;

let powerAnimation;

function createPowerAnimation(int) {
  powerAnimation = BABYLON.Animation.CreateAndStartAnimation(
    "vb",
    int.material.albedoTexture,
    "vOffset",
    10,
    30,
    0,
    1,
    1
  );

  powerAnimation.speedRatio = 0;
}

async function displayPowerAnimation({
  display,
  productObj,
  force = false,
  speed = 1,
}) {
  let int;
  let ext;
  let cover;

  if (lab === "Chill_Lab") {
    int = window.scene.getMeshByName("P_Cables_PowerFlow_Interior");
    ext = window.scene.getMeshByName("P_Cables_PowerFlow_Exterior");
  } else {
    int = window.scene.getMeshByName(
      `P_${productObj.model}_Cables_PowerFlow_Interior`
    );
    ext = window.scene.getMeshByName(
      `P_${productObj.model}_Cables_PowerFlow_Exterior`
    );
    cover = window.scene.getMeshByName(`P_${productObj.model}_Cables_Cover`);
  }

  if (display) {
    // Mostrar la partse con animación
    int.setEnabled(true);
  } else {
    // Mostrar la parte exterior
    ext.setEnabled(true);
  }

  if (!force) {
    if (lab === "Indoor_Lab") {
      showAnimation(cover, !display, speed, 0.33);
    }
    await Promise.all([
      showAnimation(int, display, speed),
      showAnimation(ext, !display, speed),
    ]);
  } else {
    int.visibility = display ? 1 : 0;
    ext.visibility = display ? 0 : 1;
    if (lab === "Indoor_Lab") {
      cover.visibility = display ? 0 : 1;
    }
  }

  if (!display) {
    int.setEnabled(false);
  } else {
    ext.setEnabled(false);
  }
}

let waterAnimationHot;
let waterAnimationCold;

function createWaterAnimation(productObj) {
  const intHot = window.scene.getMeshByName(
    `P_${productObj.model}_WaterFlow_Interior_Hot`
  );

  const intCold = window.scene.getMeshByName(
    `P_${productObj.model}_WaterFlow_Interior_Cold`
  );
  waterAnimationHot = BABYLON.Animation.CreateAndStartAnimation(
    "vb",
    intHot.material.albedoTexture,
    "vOffset",
    120,
    30,
    1,
    0,
    1
  );

  waterAnimationHot.speedRatio = 0;

  waterAnimationCold = BABYLON.Animation.CreateAndStartAnimation(
    "vb",
    intCold.material.albedoTexture,
    "vOffset",
    120,
    30,
    0,
    1,
    1
  );

  waterAnimationCold.speedRatio = 0;
}

async function displayWaterAnimation({
  productObj,
  display,
  force = false,
  speed = 1,
}) {
  const intHot = window.scene.getMeshByName(
    `P_${productObj.model}_WaterFlow_Interior_Hot`
  );
  const intCold = window.scene.getMeshByName(
    `P_${productObj.model}_WaterFlow_Interior_Cold`
  );
  const extSup = window.scene.getMeshByName(
    `P_${productObj.model}_WaterFlow_ExteriorSuperior`
  );

  if (display) {
    // Mostrar la parte con animación
    intHot.setEnabled(true);
    intCold.setEnabled(true);
  } else {
    // Mostrar la parte exterior
    extSup.setEnabled(true);
  }

  if (!force) {
    if (display) {
      await showAnimation(extSup, false, speed);
    }

    await Promise.all([
      showAnimation(intHot, display, speed),
      showAnimation(intCold, display, speed),
    ]);

    if (!display) {
      await showAnimation(extSup, true, speed);
    }
  } else {
    intHot.visibility = display ? 1 : 0;
    intCold.visibility = display ? 1 : 0;

    extSup.visibility = display ? 0 : 1;
  }

  if (display) {
    extSup.setEnabled(false);
  } else {
    intHot.setEnabled(false);
    intCold.setEnabled(false);
  }
}

let airAnimationHot;
let airAnimationCold;

function airAnimationFn(on, productObj) {
  let intHot;
  let intCold;

  if (lab === "Chill_Lab") {
    intHot = window.scene.getMeshByName("P_AirFlow_Hot");
    intCold = window.scene.getMeshByName("P_AirFlow_Cold");
  } else {
    intHot = window.scene.getMeshByName(`P_${productObj.model}_AirFlow_Hot`);
    intCold = window.scene.getMeshByName(`P_${productObj.model}_AirFlow_Cold`);
  }

  if (!on) {
    if (airAnimationHot) {
      airAnimationHot.stop();
    }

    if (airAnimationCold) {
      airAnimationCold.stop();
    }

    intHot.setEnabled(false);
    intCold.setEnabled(false);
  } else {
    airAnimationHot = BABYLON.Animation.CreateAndStartAnimation(
      "ub",
      intHot.material.albedoTexture,
      "uOffset",
      60,
      30,
      1,
      0,
      1
    );

    airAnimationHot.speedRatio = 0;

    airAnimationCold = BABYLON.Animation.CreateAndStartAnimation(
      "ub",
      intCold.material.albedoTexture,
      "uOffset",
      60,
      30,
      0,
      1,
      1
    );

    airAnimationCold.speedRatio = 0;

    intHot.setEnabled(true);
    intCold.setEnabled(true);
  }
}

let waterDropAnimation;

function waterDropAnimationFn(on) {
  const mesh = window.scene.getMeshByName("P_AFC_AD_Drop_Water");

  if (!on) {
    if (waterDropAnimation) {
      waterDropAnimation.stop();
    }

    mesh.setEnabled(false);
  } else {
    waterDropAnimation = BABYLON.Animation.CreateAndStartAnimation(
      "ub",
      mesh.material.albedoTexture,
      "uOffset",
      60,
      30,
      1,
      0,
      1
    );

    waterDropAnimation.speedRatio = 0;

    mesh.setEnabled(true);
  }
}

let fansAnimation = [];

async function changeSpeedAnimation({
  speed,
  msTransition,
  force = false,
  water = false,
  air = false,
  drops = false,
  fans = false,
  cables = false,
}) {
  if (!force) {
    let numberOfTransitions = msTransition / 100;

    let speedWaterHot = 0;
    let speedWaterCold = 0;
    let speedAirHot = 0;
    let speedAirCold = 0;
    let speedDrops = 0;
    let speedFans = 0;
    let speedCables = 0;

    if (water) {
      if (waterAnimationHot) {
        speedWaterHot = waterAnimationHot.speedRatio;
      }
      if (waterAnimationCold) {
        speedWaterCold = waterAnimationCold.speedRatio;
      }
    }

    if (air) {
      if (airAnimationHot) {
        speedAirHot = airAnimationHot.speedRatio;
      }
      if (airAnimationCold) {
        speedAirCold = airAnimationCold.speedRatio;
      }
    }

    if (drops && waterDropAnimation) {
      speedDrops = waterDropAnimation.speedRatio;
    }

    if (fans && fansAnimation[0]) {
      speedFans = fansAnimation[0].speedRatio;
    }

    if (cables && powerAnimation) {
      speedCables = powerAnimation.speedRatio;
    }

    const differenceWaterHot = speed - speedWaterHot;
    const differenceWaterCold = speed - speedWaterCold;
    const differenceAirHot = speed - speedAirHot;
    const differenceAirCold = speed - speedAirCold;
    const differenceDrops = speed - speedDrops;
    const differenceFans = speed - speedFans;
    const differenceCables = speed - speedCables;

    const stepWaterHot = differenceWaterHot / numberOfTransitions;
    const stepWaterCold = differenceWaterCold / numberOfTransitions;
    const stepAirHot = differenceAirHot / numberOfTransitions;
    const stepAirCold = differenceAirCold / numberOfTransitions;
    const stepDrops = differenceDrops / numberOfTransitions;
    const stepFans = differenceFans / numberOfTransitions;
    const stepCables = differenceCables / numberOfTransitions;

    const controlMult = 10000000000;

    while (numberOfTransitions > 0) {
      if (water) {
        if (waterAnimationHot) {
          speedWaterHot =
            (speedWaterHot * controlMult + stepWaterHot * controlMult) /
            controlMult;
          waterAnimationHot.speedRatio = speedWaterHot;
        }

        if (waterAnimationCold) {
          speedWaterCold =
            (speedWaterCold * controlMult + stepWaterCold * controlMult) /
            controlMult;
          waterAnimationCold.speedRatio = speedWaterCold;
        }
      }

      if (air) {
        if (airAnimationHot) {
          speedAirHot =
            (speedAirHot * controlMult + stepAirHot * controlMult) /
            controlMult;
          airAnimationHot.speedRatio = speedAirHot;
        }

        if (airAnimationCold) {
          speedAirCold =
            (speedAirCold * controlMult + stepAirCold * controlMult) /
            controlMult;
          airAnimationCold.speedRatio = speedAirCold;
        }
      }

      if (drops && waterDropAnimation) {
        speedDrops =
          (speedDrops * controlMult + stepDrops * controlMult) / controlMult;

        waterDropAnimation.speedRatio = speedDrops;
      }

      if (fans && fansAnimation[0]) {
        speedFans =
          (speedFans * controlMult + stepFans * controlMult) / controlMult;

        for (let index = 0; index < fansAnimation.length; index++) {
          const el = fansAnimation[index];
          el.speedRatio = speedFans;
        }
      }

      if (cables && powerAnimation) {
        speedCables =
          (speedCables * controlMult + stepCables * controlMult) / controlMult;

        powerAnimation.speedRatio = speedCables;
      }

      numberOfTransitions--;
      await sleep(100);
    }
  } else {
    if (water) {
      if (waterAnimationHot) {
        waterAnimationHot.speedRatio = speed;
      }
      if (waterAnimationCold) {
        waterAnimationCold.speedRatio = speed;
      }
    }

    if (air) {
      if (airAnimationHot) {
        airAnimationHot.speedRatio = speed;
      }
      if (airAnimationCold) {
        airAnimationCold.speedRatio = speed;
      }
    }

    if (drops && waterDropAnimation) {
      waterDropAnimation.speedRatio = speed;
    }

    if (fans && fansAnimation[0]) {
      for (let index = 0; index < fansAnimation.length; index++) {
        const el = fansAnimation[index];
        el.speedRatio = speed;
      }
    }

    if (cables && powerAnimation) {
      powerAnimation.speedRatio = speed;
    }
  }
}

function fanAnimationFn(on, speed = 0) {
  if (process.env.NODE_ENV === "development") {
    speed *= window.speedFactor || speed;
  }

  if (on) {
    fansAnimation = [
      window.scene.getAnimationGroupByName("P_AFCAD_Fan_Blades_1Action"),
      window.scene.getAnimationGroupByName("P_AFCAD_Fan_Blades_2Action"),
      window.scene.getAnimationGroupByName("P_AFCAD_Fan_Blades_3Action"),
      window.scene.getAnimationGroupByName("P_AFCAD_Fan_Blades_4Action"),
      window.scene.getAnimationGroupByName("P_AFCAD_Fan_Blades_5Action"),
      window.scene.getAnimationGroupByName("P_AFCAD_Fan_Blades_6Action"),
      window.scene.getAnimationGroupByName("P_AFCAD_Fan_Blades_7Action"),
      window.scene.getAnimationGroupByName("P_AFCAD_Fan_Blades_8Action"),
      window.scene.getAnimationGroupByName("P_AFCAD_Fan_Blades_9Action"),
      window.scene.getAnimationGroupByName("P_AFCAD_Fan_Blades_10Action"),

      window.scene.getAnimationGroupByName("P_AFCNA_Fan_Blades_1Action"),
      window.scene.getAnimationGroupByName("P_AFCNA_Fan_Blades_2Action"),
      window.scene.getAnimationGroupByName("P_AFCNA_Fan_Blades_3Action"),
      window.scene.getAnimationGroupByName("P_AFCNA_Fan_Blades_4Action"),
      window.scene.getAnimationGroupByName("P_AFCNA_Fan_Blades_5Action"),
      window.scene.getAnimationGroupByName("P_AFCNA_Fan_Blades_6Action"),
      window.scene.getAnimationGroupByName("P_AFCNA_Fan_Blades_7Action"),
      window.scene.getAnimationGroupByName("P_AFCNA_Fan_Blades_8Action"),
      window.scene.getAnimationGroupByName("P_AFCNA_Fan_Blades_9Action"),
      window.scene.getAnimationGroupByName("P_AFCNA_Fan_Blades_10Action"),

      // TODO check
      window.scene.getAnimationGroupByName("P_CWA_Fan1Action"),
      window.scene.getAnimationGroupByName("P_CWA_Fan2Action"),

      // TODO check
      window.scene.getAnimationGroupByName("P_PDX_Fan_1Action.002"),
      window.scene.getAnimationGroupByName("P_PDX_Fan_2Action.002"),
      window.scene.getAnimationGroupByName("P_PDX_Fan_3Action.002"),
    ];

    fansAnimation = fansAnimation.filter((el) => el);

    for (let index = 0; index < fansAnimation.length; index++) {
      const el = fansAnimation[index];
      el.speedRatio = speed;
      el.play(true);
    }
  } else {
    for (let index = 0; index < fansAnimation.length; index++) {
      const el = fansAnimation[index];
      el.pause();
    }
  }
}

let cancelCTA = false;

async function actionCTA(on, productObj, cta) {
  if (on) {
    cancelCTA = false;
    switch (cta) {
      case "power":
        // TODO check naming P_Power_Connection Indoor_Lab
        if (!cancelCTA) {
          highlight("P_Power_Connection", "orange", true, false, 1);
        }

        if (lab === "Chill_Lab") {
          if (!cancelCTA) {
            highlight("P_Cables_PowerFlow_Exterior", "orange", true, false, 1);
          }

          if (!cancelCTA) {
            await highlight(
              "P_Power_Connection_Leds",
              "orange",
              true,
              false,
              1
            );
          }

          if (!cancelCTA) {
            highlight("P_Power_Connection_Leds", "green", true, true, 2);
          }
        } else if (lab === "Indoor_Lab") {
          if (!cancelCTA) {
            await highlight(
              `P_${productObj.model}_Cables_PowerFlow_Exterior`,
              "orange",
              true,
              false,
              1
            );
          }
        }

        if (!cancelCTA) {
          displayPowerAnimation({ display: true, productObj });
        }

        if (!cancelCTA) {
          await changeSpeedAnimation({
            speed: 1,
            msTransition: 3000,
            cables: true,
          });
        }
        break;
      case "hydraulic":
        if (productObj.model === "PDX") {
          if (!cancelCTA) {
            highlight("P_PDX_Heat_Exchanger", "orange", true, false, 1);
          }
        }

        if (!cancelCTA) {
          await highlight("P_Water_Tottem", "orange", true, false, 1);
        }

        if (!cancelCTA) {
          highlight(
            `P_${productObj.model}_WaterFlow_ExteriorSuperior`,
            "orange",
            true,
            false,
            1
          );
        }

        if (!cancelCTA) {
          await displayWaterAnimation({
            display: true,
            force: true,
            productObj,
          });
        }

        if (!cancelCTA) {
          await changeSpeedAnimation({
            speed: 1,
            msTransition: 3000,
            force: true,
            water: true,
          });
        }
        break;
      case "thermoregulation":
      case "airDuct":
        if (lab === "Chill_Lab") {
          if (!cancelCTA) {
            highlight("P_Windows", "orange", true, true, 1);
          }
        } else if (lab === "Indoor_Lab") {
          if (cta === "thermoregulation") {
            if (!cancelCTA) {
              highlight("P_Thermo_Regulation", "orange", true, true, 1); // TODO Ask if this needs to be orange as well
            }
          } else if (cta === "airDuct") {
            if (!cancelCTA) {
              highlight("P_AirFlow_Connection", "orange", true, true, 1); // TODO Ask if this needs to be orange as well
            }
          }
        }

        if (lab === "Chill_Lab") {
          if (!cancelCTA) {
            await highlight(
              `P_${productObj.model}_Fans`,
              "orange",
              true,
              false,
              1
            );
          }
        } else if (lab === "Indoor_Lab") {
          if (!cancelCTA) {
            await highlight(
              `P_${productObj.model}_Fan_1`,
              "orange",
              true,
              false,
              1
            );
          }

          if (!cancelCTA) {
            fanAnimationFn(true);
          }
        }

        if (!cancelCTA) {
          await loadAirFlow(true, true, productObj);
        }

        if (!cancelCTA) {
          await changeSpeedAnimation({
            speed: 1,
            msTransition: 3000,
            force: true,
            water: true,
            air: true,
            fans: true,
          });
        }
        break;
    }
  } else {
    cancelCTA = true;
    switch (cta) {
      case "power":
        if (lab === "Chill_Lab") {
          highlight("P_Power_Connection_Leds", "green", false, true, 2);
        }
        displayPowerAnimation({ display: false, productObj });
        break;
      case "hydraulic":
        displayWaterAnimation({ display: false, force: true, productObj });
        break;
      case "thermoregulation":
      case "airDuct":
        if (lab === "Chill_Lab") {
          highlight("P_Windows", "orange", false, true, 1);
        } else if (lab === "Indoor_Lab") {
          if (cta === "thermoregulation") {
            highlight("P_Thermo_Regulation", "orange", false, true, 1);
          } else if (cta === "airDuct") {
            highlight("P_AirFlow_Connection", "orange", false, true, 1);
          }

          fanAnimationFn(false);
        }
        loadAirFlow(false, true, productObj);
    }
  }
}

async function powerOnProduct(productObj, force = false, mode = "product") {
  if (!force) {
    if (lab === "Chill_Lab") {
      await highlight("P_Cables_PowerFlow_Exterior", "orange", true, false, 1);
    } else {
      await highlight(
        `P_${productObj.model}_Cables_PowerFlow_Exterior`,
        "orange",
        true,
        false,
        1
      );
    }

    airAnimationFn(true, productObj);

    await highlight(
      `P_${productObj.model}_WaterFlow_ExteriorSuperior`,
      "orange",
      true,
      false,
      1
    );
  } else {
    switch (mode) {
      case "product":
        airAnimationFn(true, productObj);
        break;
      case "power":
        // TODO
        if (lab === "Chill_Lab") {
          highlight("P_Power_Connection", "orange", true, false, 1);
          await highlight("P_Power_Connection_Leds", "orange", true, false, 1);
          highlight("P_Power_Connection_Leds", "green", true, true, 2);
        } else {
          highlight(
            `P_${productObj.model}_Power_Connection`,
            "orange",
            true,
            false,
            1
          );
        }

        displayPowerAnimation({ display: true, productObj });
        await changeSpeedAnimation({
          speed: 1,
          msTransition: 3000,
          cables: true,
        });
        break;
      case "hydraulic":
        await highlight("P_Water_Tottem", "orange", true, false, 1);
        await displayWaterAnimation({ display: true, force: true, productObj });
        await changeSpeedAnimation({
          speed: 1,
          msTransition: 3000,
          force: true,
          water: true,
        });
        break;
      case "thermoregulation":
      case "airDuct":
        if (lab === "Chill_Lab") {
          highlight("P_Windows", "orange", true, true, 1);
        }
        await highlight(`P_${productObj.model}_Fans`, "orange", true, false, 1);

        // fanAnimationFn(true, 0);
        await loadAirFlow(true, force, productObj);
        airAnimationFn(true, productObj);
        await changeSpeedAnimation({
          speed: 1,
          msTransition: 3000,
          force: true,
          water: true,
          air: true,
          fans: true,
        });
        break;
    }
  }

  setCamera("Welcome");
  lookingAt = "host";
  player.lookAt(host.absolutePosition);
  cc.enableKeyBoard(true);
}

async function powerOffProduct(productObj, force = false) {
  // await changeSpeedAnimation({
  //   speed: 0,
  //   msTransition: 3000,
  //   force: force,
  //   water: true,
  //   air: true,
  //   fans: true,
  //   cables: true,
  // });

  await Promise.all([
    await loadAirFlow(false, force, productObj),
    await displayWaterAnimation({ display: false, force, productObj }),
  ]);

  airAnimationFn(false, productObj);
  fanAnimationFn(false);

  highlight("P_Power_Connection_Leds", "green", false, true, 2);
  highlight("P_Windows", "green", false, true, 1);

  displayPowerAnimation({ display: false, force, productObj });
}

let cancelAnimation = false;

function setCancelAnimation() {
  cancelAnimation = true;
}

async function startTest(test, productObj, callbackScene) {
  cancelAnimation = false;

  callbackScene({ key: "showSpinner", value: true });

  await securePlayer(true, "product");

  displayHost(false);
  displayPlayer(false);

  callbackScene({ key: "showSpinner", value: false });

  switch (test) {
    case "performance":
      await performanceTest(callbackScene, productObj);
      break;
    case "noise":
      await noiseTest(callbackScene, productObj);
      break;
    case "adiabatic":
      await adiabaticTest(callbackScene, productObj);
      break;
    case "fast":
      await fastTest(callbackScene, productObj);
      break;
    case "transient":
      await transientTest(callbackScene, productObj);
      break;
  }

  displayHost(true);
  displayPlayer(true);
}

async function performanceTest(callbackScene, productObj) {
  await prePerformanceTest(productObj);

  if (!cancelAnimation) {
    await new Promise((resolve) => {
      callbackScene({ key: "displayVideo", value: true, resolve });
    });
  } else {
    return await performanceTestEnd({ productObj, fast: true, callbackScene });
  }

  if (!cancelAnimation) {
    await new Promise((resolve) => {
      callbackScene({ key: "playVideo", resolve });
    });
  } else {
    return await performanceTestEnd({ productObj, fast: true, callbackScene });
  }

  console.log("Start test animations");

  const dateStart = Date.now();
  let currentDate;
  let currentTiming = 0;

  while (!cancelAnimation && currentTiming < 2000) {
    currentDate = Date.now();
    currentTiming = currentDate - dateStart;
    await sleep(100);
  }

  displayPowerAnimation({ display: true, productObj });

  while (!cancelAnimation && currentTiming < 40000) {
    currentDate = Date.now();
    currentTiming = currentDate - dateStart;
    await sleep(100);
  }

  await performanceTestEnd({
    productObj,
    fast: cancelAnimation,
    callbackScene,
  });
}

async function prePerformanceTest(productObj) {
  await changeSpeedAnimation({
    speed: 1,
    force: true,
    water: true,
    air: true,
    drops: false,
    cables: true,
    fans: true,
  });

  displayWaterAnimation({ display: true, productObj });
  loadAirFlow(true, false, productObj);
}

async function performanceTestEnd({ productObj, fast = false, callbackScene }) {
  // await changeSpeedAnimation({
  //   speed: 0,
  //   force: fast,
  //   msTransition: 1000,
  //   water: true,
  //   air: true,
  //   fans: true,
  // });

  await Promise.all([
    loadAirFlow(false, fast, productObj),
    displayWaterAnimation({ display: false, productObj, force: fast }),
    displayPowerAnimation({ display: false, productObj, force: fast }),
  ]);

  callbackScene({ key: "close", value: fast });
}

async function noiseTest(callbackScene, productObj) {
  const meshC = window.scene.getMeshByName("P_AirFlow_Cold");
  const meshCollider = window.scene.getMeshByName("P_AFC_SoundPanel_Colliders");
  const AFC_SoundPanel = window.scene.getTransformNodeByName("AFC_SoundPanel");

  await preNoiseTest({ productObj, meshC, meshCollider, AFC_SoundPanel });

  if (!cancelAnimation) {
    await new Promise((resolve) => {
      callbackScene({ key: "displayVideo", value: true, resolve });
    });
  } else {
    return await noiseTestEnd({
      productObj,
      meshC,
      meshCollider,
      AFC_SoundPanel,
      fast: true,
      dateStart: null,
      callbackScene,
    });
  }

  if (!cancelAnimation) {
    await new Promise((resolve) => {
      callbackScene({ key: "playVideo", resolve });
    });
  } else {
    return await noiseTestEnd({
      productObj,
      meshC,
      meshCollider,
      AFC_SoundPanel,
      fast: true,
      dateStart: null,
      callbackScene,
    });
  }

  console.log("Start test animations");

  const dateStart = Date.now();
  let currentDate;
  let currentTiming = 0;

  if (!cancelAnimation) {
    await changeSpeedAnimation({
      speed: 1,
      msTransition: 2500,
      air: true,
      fans: true,
    });
  } else {
    return await noiseTestEnd({
      productObj,
      meshC,
      meshCollider,
      AFC_SoundPanel,
      fast: true,
      dateStart,
      callbackScene,
    });
  }

  while (!cancelAnimation && currentTiming < 45000) {
    currentDate = Date.now();
    currentTiming = currentDate - dateStart;
    await sleep(100);
  }

  await noiseTestEnd({
    productObj,
    meshC,
    meshCollider,
    AFC_SoundPanel,
    fast: cancelAnimation,
    dateStart,
    callbackScene,
  });
}

async function preNoiseTest({
  productObj,
  meshC,
  meshCollider,
  AFC_SoundPanel,
}) {
  meshCollider.setEnabled(true);
  await loadAirFlow(true, true, productObj);
  showAnimation(meshC, false, 0.4); // Hide cold Air

  if (!cancelAnimation) {
    AFC_SoundPanel.setEnabled(true);

    const promises = [];

    for (let index = 1; index <= 10; index++) {
      promises.push(showAnimation(`P_AFC_SoundPanel_${index}`, true, 0.4));
    }

    await Promise.all(promises);
    // soundPanelAnimation(true);
  } else {
    return await noiseTestEnd({
      productObj,
      meshC,
      meshCollider,
      AFC_SoundPanel,
      fast: true,
      dateStart: null,
    });
  }
}

async function noiseTestEnd({
  meshCollider,
  AFC_SoundPanel,
  fast,
  callbackScene,
  productObj,
}) {
  // await changeSpeedAnimation({
  //   speed: 0,
  //   msTransition: 1000,
  //   force: fast,
  //   air: true,
  //   fans: true,
  // });

  // await soundPanelAnimation(false);
  const promises = [loadAirFlow(false, fast, productObj)];

  for (let index = 1; index <= 10; index++) {
    promises.push(showAnimation(`P_AFC_SoundPanel_${index}`, false, 0.4));
  }

  if (!fast) {
    await Promise.all(promises);
  }

  AFC_SoundPanel.setEnabled(false);

  meshCollider.setEnabled(false);

  callbackScene({ key: "close", value: fast });
}

async function adiabaticTest(callbackScene, productObj) {
  await preAdiabaticTest(productObj);

  if (!cancelAnimation) {
    await new Promise((resolve) => {
      callbackScene({ key: "displayVideo", value: true, resolve });
    });
  } else {
    return await adiabaticTestEnd({
      fast: true,
      callbackScene,
      productObj,
    });
  }

  if (!cancelAnimation) {
    await new Promise((resolve) => {
      callbackScene({ key: "playVideo", resolve });
    });
  } else {
    return await adiabaticTestEnd({
      fast: true,
      callbackScene,
      productObj,
    });
  }

  console.log("Start test animations");

  const dateStart = Date.now();
  let currentDate;
  let currentTiming = 0;

  while (!cancelAnimation && currentTiming < 9000) {
    currentDate = Date.now();
    currentTiming = currentDate - dateStart;
    await sleep(100);
  }

  if (!cancelAnimation) {
    await Promise.all([
      highlight("P_AFC_AD_Pads", "orange", true, false, 1.5),
      loadWaterDrop(true, false),
    ]);
  } else {
    return await adiabaticTestEnd({ fast: true, callbackScene, productObj });
  }

  if (!cancelAnimation) {
    waterDropAnimationFn(true);
  } else {
    return await adiabaticTestEnd({ fast: true, callbackScene, productObj });
  }

  if (!cancelAnimation) {
    await changeSpeedAnimation({
      speed: 1,
      msTransition: 1000,
      drops: true,
    });
  } else {
    return await adiabaticTestEnd({ fast: true, callbackScene, productObj });
  }

  while (!cancelAnimation && currentTiming < 40000) {
    currentDate = Date.now();
    currentTiming = currentDate - dateStart;
    await sleep(100);
  }

  await adiabaticTestEnd({ fast: cancelAnimation, callbackScene, productObj });
}

async function preAdiabaticTest(productObj) {
  await changeSpeedAnimation({
    speed: 1,
    force: true,
    water: true,
    air: true,
    drops: false,
    fans: true,
  });
  displayWaterAnimation({ display: true, productObj });
  loadAirFlow(true, false, productObj);
}

async function adiabaticTestEnd({ fast, productObj, callbackScene }) {
  // await changeSpeedAnimation({
  //   speed: 0,
  //   force: fast,
  //   msTransition: 1000,
  //   water: true,
  //   air: true,
  //   drops: true,
  //   fans: true,
  // });

  await loadAirFlow(false, fast, productObj);
  await loadWaterDrop(false, fast);
  await displayWaterAnimation({ display: false, productObj, force: fast });

  callbackScene({ key: "close", value: fast });
}

async function fastTest(callbackScene, productObj) {
  await preFastTest(productObj);

  if (!cancelAnimation) {
    await new Promise((resolve) => {
      callbackScene({ key: "displayVideo", value: true, resolve });
    });
  } else {
    return await adiabaticTestEnd({
      fast: true,
      callbackScene,
      productObj,
    });
  }

  if (!cancelAnimation) {
    await new Promise((resolve) => {
      callbackScene({ key: "playVideo", resolve });
    });
  } else {
    return await adiabaticTestEnd({
      fast: true,
      callbackScene,
      productObj,
    });
  }

  console.log("Start test animations");

  const dateStart = Date.now();
  let currentDate;
  let currentTiming = 0;

  displayPowerAnimation({ display: true, productObj });

  changeSpeedAnimation({
    speed: 1,
    msTransition: 2000,
    cables: true,
  });

  let msStartPowerFailure;
  let msEndPowerFailure;

  if (productObj.model === "PDX") {
    msStartPowerFailure = 5000;
    msEndPowerFailure = 10000;
  } else if (productObj.model === "CWA") {
    msStartPowerFailure = 1500;
    msEndPowerFailure = 5500;
  } else {
    msStartPowerFailure = 5500;
    msEndPowerFailure = 8000;
  }

  while (!cancelAnimation && currentTiming < msStartPowerFailure) {
    currentDate = Date.now();
    currentTiming = currentDate - dateStart;
    await sleep(100);
  }

  if (!cancelAnimation) {
    displayPowerAnimation({ display: false, productObj });
  } else {
    return await fastTestEnd(callbackScene, true, productObj);
  }

  if (!cancelAnimation) {
    highlight("P_Red_Light_Machine", "red", true, true, 4);
  } else {
    return await fastTestEnd(callbackScene, true, productObj);
  }

  if (!cancelAnimation) {
    // await changeSpeedAnimation({
    //   speed: 0,
    //   msTransition: 500,
    //   force: false,
    //   water: true,
    //   air: true,
    //   fans: true,
    // });
    loadAirFlow(false, false, productObj);
  } else {
    return await fastTestEnd(callbackScene, true, productObj);
  }

  if (!cancelAnimation) {
    await displayWaterAnimation({ display: false, productObj }); // check
  } else {
    return await fastTestEnd(callbackScene, true, productObj);
  }

  while (!cancelAnimation && currentTiming < msEndPowerFailure) {
    currentDate = Date.now();
    currentTiming = currentDate - dateStart;
    await sleep(100);
  }

  if (!cancelAnimation) {
    highlight("P_Red_Light_Machine", "red", false, true, 4);
  } else {
    return await fastTestEnd(callbackScene, true, productObj);
  }

  if (lab === "Chill_Lab") {
    if (!cancelAnimation) {
      highlight("P_Orange_Light_Machine", "orange", true, true, 4);
    } else {
      return await fastTestEnd(callbackScene, true, productObj);
    }
  } else if (lab === "Indoor_Lab") {
    if (!cancelAnimation) {
      highlight("P_Red_Light_Machine", "orange", true, true, 4);
    } else {
      return await fastTestEnd(callbackScene, true, productObj);
    }
  }

  while (!cancelAnimation && currentTiming < 10000) {
    currentDate = Date.now();
    currentTiming = currentDate - dateStart;
    await sleep(100);
  }

  if (lab === "Chill_Lab") {
    if (!cancelAnimation) {
      await highlight("P_Orange_Light_Machine", "orange", false, true, 4);
    } else {
      return await fastTestEnd(callbackScene, true, productObj);
    }
  } else if (lab === "Indoor_Lab") {
    if (!cancelAnimation) {
      await highlight("P_Red_Light_Machine", "orange", false, true, 4);
    } else {
      return await fastTestEnd(callbackScene, true, productObj);
    }
  }

  if (lab === "Chill_Lab") {
    if (!cancelAnimation) {
      highlight("P_Orange_Light_Machine", "green", true, true, 4);
    } else {
      return await fastTestEnd(callbackScene, true, productObj);
    }
  } else if (lab === "Indoor_Lab") {
    if (!cancelAnimation) {
      highlight("P_Red_Light_Machine", "green", true, true, 4);
    } else {
      return await fastTestEnd(callbackScene, true, productObj);
    }
  }

  if (!cancelAnimation) {
    await displayPowerAnimation({ display: true, force: true, productObj });
  } else {
    return await fastTestEnd(callbackScene, true, productObj);
  }

  if (!cancelAnimation) {
    await changeSpeedAnimation({
      speed: 1,
      force: true,
      msTransition: 1000,
      cables: true,
    });
  } else {
    return await fastTestEnd(callbackScene, true, productObj);
  }

  if (!cancelAnimation) {
    // changeSpeedAnimation({
    //   speed: 1,
    //   force: false,
    //   msTransition: 1000,
    //   water: true,
    //   air: true,
    //   fans: true,
    // });
    loadAirFlow(true, false, productObj);
  } else {
    return await fastTestEnd(callbackScene, true, productObj);
  }

  if (!cancelAnimation) {
    displayWaterAnimation({ display: true, productObj });
  } else {
    return await fastTestEnd(callbackScene, true, productObj);
  }

  // if (!cancelAnimation) {
  //   await loadCompressor({ load: true, productObj });
  // } else {
  //   return await fastTestEnd(callbackScene, true, productObj);
  // }

  if (!cancelAnimation) {
    await highlight(
      `P_${productObj.model}_Compressor`,
      "orange",
      true,
      false,
      1
    );
  } else {
    return await fastTestEnd(callbackScene, true, productObj);
  }

  if (!cancelAnimation) {
    await highlight(
      `P_${productObj.model}_Compressor`,
      "orange",
      true,
      false,
      1
    );
  } else {
    return await fastTestEnd(callbackScene, true, productObj);
  }

  while (!cancelAnimation && currentTiming < 41000) {
    currentDate = Date.now();
    currentTiming = currentDate - dateStart;
    await sleep(100);
  }

  await fastTestEnd(callbackScene, cancelAnimation, productObj);
}

async function preFastTest(productObj) {
  await changeSpeedAnimation({
    speed: 1,
    force: true,
    water: true,
    air: true,
    drops: false,
    fans: true,
  });
  displayWaterAnimation({ display: true, productObj });
  loadAirFlow(true, false, productObj);
}

async function fastTestEnd(callbackScene, fast, productObj) {
  highlight("P_Red_Light_Machine", "red", false, true, 3);
  highlight("P_Red_Light_Machine", "orange", false, true, 3);
  highlight("P_Red_Light_Machine", "green", false, true, 3);
  highlight("P_Orange_Light_Machine", "orange", false, true, 3);
  highlight("P_Orange_Light_Machine", "green", false, true, 3);

  // await changeSpeedAnimation({
  //   speed: 0,
  //   force: fast,
  //   msTransition: 1000,
  //   water: true,
  //   air: true,
  //   fans: true,
  // });

  await Promise.all([
    loadAirFlow(false, fast, productObj),
    displayWaterAnimation({ display: false, productObj, force: fast }),
    // loadCompressor({ load: false, productObj, force: fast }),
    displayPowerAnimation({ display: false, force: fast, productObj }),
  ]);

  callbackScene({ key: "close", value: fast });
}

async function transientTest(callbackScene, productObj) {
  await preTransientTest({ callbackScene, productObj });

  if (!cancelAnimation) {
    await new Promise((resolve) => {
      callbackScene({ key: "displayVideo", value: true, resolve });
    });
  } else {
    return await transientTestEnd({ productObj, fast: true, callbackScene });
  }

  if (!cancelAnimation) {
    await new Promise((resolve) => {
      callbackScene({ key: "playVideo", resolve });
    });
  } else {
    return await transientTestEnd({ productObj, fast: true, callbackScene });
  }

  console.log("Start test animations");

  const dateStart = Date.now();
  let currentDate;
  let currentTiming = 0;

  const airHot = window.scene.getMeshByName("P_AirFlow_Cold");
  const compressor = window.scene.getMeshByName(
    `P_${productObj.model}_Compressor`
  );
  const coils = window.scene.getMeshByName(`P_${productObj.model}_VCoils`);

  while (!cancelAnimation && currentTiming < 15500) {
    // 11500
    // TODO CHECK a little later in sync with the spike
    currentDate = Date.now();
    currentTiming = currentDate - dateStart;
    await sleep(100);
  }

  if (!cancelAnimation) {
    // Naranja
    colorAnimation({
      mesh: airHot,
      color: "#E98334",
    });
  } else {
    return await transientTestEnd({ fast: true, productObj, callbackScene });
  }

  if (!cancelAnimation) {
    await highlight(compressor, "yellow", true, true, 1);
  } else {
    return await transientTestEnd({ fast: true, productObj, callbackScene });
  }

  // if (!cancelAnimation) {
  //   showAnimation(compressor, true, 0.4);
  // } else {
  //   return await transientTestEnd({ fast: true, productObj, callbackScene });
  // }

  while (!cancelAnimation && currentTiming < 30000) {
    currentDate = Date.now();
    currentTiming = currentDate - dateStart;
    await sleep(100);
  }

  if (!cancelAnimation) {
    // Rojo
    colorAnimation({
      mesh: airHot,
      color: "#E93440",
    });
  } else {
    return await transientTestEnd({ fast: true, productObj, callbackScene });
  }

  if (!cancelAnimation) {
    await highlight(compressor, "yellow", false, true, 1); // TODO ask if this needs to be orange
  } else {
    return await transientTestEnd({ fast: true, productObj, callbackScene });
  }

  if (!cancelAnimation) {
    await highlight(coils, "orange", false, true, 1);
  } else {
    return await transientTestEnd({ fast: true, productObj, callbackScene });
  }

  if (!cancelAnimation) {
    await highlight(compressor, "orange", true, true, 1);
  } else {
    return await transientTestEnd({ fast: true, productObj, callbackScene });
  }

  while (!cancelAnimation && currentTiming < 40000) {
    currentDate = Date.now();
    currentTiming = currentDate - dateStart;
    await sleep(100);
  }

  await transientTestEnd({ fast: cancelAnimation, productObj, callbackScene });
}

async function preTransientTest({ callbackScene, productObj }) {
  const product = window.scene.getMeshByName(`P_${productObj.model}`);
  const coils = window.scene.getMeshByName(`P_${productObj.model}_VCoils`);
  const pads = window.scene.getMeshByName(`P_${productObj.model}_Pads`);
  // const compressor = window.scene.getMeshByName(
  //   `P_${productObj.model}_Compressor`
  // );

  await changeSpeedAnimation({
    speed: 1,
    force: true,
    water: true,
    air: true,
    drops: false,
    fans: true,
  });
  displayWaterAnimation({ display: true, productObj });
  loadAirFlow(true, false, productObj);

  if (!cancelAnimation) {
    await Promise.all([
      showAnimation(product, false, 1, 0.33),
      showAnimation(pads, false, 1, 0.33),
      // showAnimation(compressor, false, 0.4, 0.33),
    ]);
  } else {
    return await transientTestEnd({ fast: true, productObj, callbackScene });
  }

  if (!cancelAnimation) {
    await highlight(coils, "orange", true, true, 1);
  } else {
    return await transientTestEnd({ fast: true, productObj, callbackScene });
  }
}

async function transientTestEnd({ fast, productObj, callbackScene }) {
  const airHot = window.scene.getMeshByName("P_AirFlow_Cold");
  const product = window.scene.getMeshByName(`P_${productObj.model}`);
  const pads = window.scene.getMeshByName(`P_${productObj.model}_Pads`);
  const compressor = window.scene.getMeshByName(
    `P_${productObj.model}_Compressor`
  );
  const coils = window.scene.getMeshByName(`P_${productObj.model}_VCoils`);

  await highlight(coils, "orange", false, true, 1);
  await highlight(compressor, "yellow", false, true, 1);
  await highlight(compressor, "orange", false, true, 1);

  showAnimation(product, true, 1);
  showAnimation(pads, true, 1);
  // showAnimation(compressor, true, 0.4);
  colorAnimation({
    mesh: airHot,
    color: "#33BBFF",
  });
  // await changeSpeedAnimation({
  //   speed: 0,
  //   force: fast,
  //   msTransition: 1000,
  //   water: true,
  //   air: true,
  //   fans: true,
  // });

  await loadAirFlow(false, fast, productObj);
  await displayWaterAnimation({ display: false, productObj, force: fast });

  callbackScene({ key: "close", value: fast });
}

let soundPlayer;

function playSound(name, sound) {
  let playbackRate = 1;
  if (process.env.NODE_ENV === "development") {
    playbackRate = window.speedFactor || 1;
  }

  soundPlayer = new BABYLON.Sound(
    name,
    BASE_URL + "sounds/" + sound,
    window.scene,
    null,
    {
      autoplay: true,
      playbackRate,
    }
  );
}

function stopSound() {
  if (soundPlayer) {
    soundPlayer.dispose();
  }
}

function playLoopSound(loopName, sound) {
  let playbackRate = 1;
  if (process.env.NODE_ENV === "development") {
    playbackRate = window.speedFactor || 1;
  }

  soundPlayer = new BABYLON.Sound(
    loopName,
    BASE_URL + "sounds/" + sound,
    window.scene,
    null,
    {
      loop: true,
      autoplay: true,
      playbackRate,
    }
  );
}

let animationsHighlight = [];

const colors = {
  electric: ["#53699b", "#787748"],
  blue: ["#0c283d", "#2986cc"],
  green: ["#002200", "#00F500"],
  orange: ["#663D00", "#D88000"],
  yellow: ["#3b3b00", "#ffff00"],
  red: ["#020000", "#CE2029"],
};

function highlight(mesh, color, on, loop, speed = 1.5) {
  if (process.env.NODE_ENV === "development") {
    speed *= window.speedFactor || 1;
  }

  if (typeof mesh === "string") {
    mesh = window.scene.getMeshByName(mesh);
  }

  if (!mesh) {
    console.error(`not existing`);
    return;
  }

  const oldAnimation = animationsHighlight.find((el) => el.mesh === mesh.name);
  if (oldAnimation) {
    if (!on) {
      mesh.material.emissiveColor = BABYLON.Color3.Black();
      oldAnimation.animation.stop();
    }

    return;
  } else if (!on) {
    console.error("animation non existing");
    return;
  }

  const keys = [
    {
      frame: 0,
      value: BABYLON.Color3.Black(),
    },
  ];

  keys.push(
    ...colors[color].map((el, index) => {
      return {
        frame: index + 1,
        value: BABYLON.Color3.FromHexString(el).toLinearSpace(),
      };
    })
  );

  keys.push({
    frame: keys.length,
    value: BABYLON.Color3.Black(),
  });

  const animation = new BABYLON.Animation(
    "animation",
    "material.emissiveColor",
    speed,
    BABYLON.Animation.ANIMATIONTYPE_COLOR3,
    loop
      ? BABYLON.Animation.ANIMATIONLOOPMODE_CYCLE
      : BABYLON.Animation.ANIMATIONLOOPMODE_CONSTANT
  );

  animation.setKeys(keys);

  let promise;
  let animatable;

  if (loop) {
    animatable = window.scene.beginDirectAnimation(
      mesh,
      [animation],
      0,
      keys.length - 1,
      true,
      speed,
      () => {
        animationsHighlight = animationsHighlight.filter(
          (el) => el.mesh !== mesh.name
        );
      }
    );
  } else {
    promise = new Promise((resolve) => {
      animatable = window.scene.beginDirectAnimation(
        mesh,
        [animation],
        0,
        keys.length - 1,
        false,
        speed,
        () => {
          animationsHighlight = animationsHighlight.filter(
            (el) => el.mesh !== mesh.name
          );
          resolve();
        }
      );
    });
  }

  animationsHighlight.push({
    animation: animatable,
    mesh: mesh.name,
  });

  return promise;
}

const runScene = () => {
  const rails = window.scene.getMeshByName("P_Rails");

  engine.runRenderLoop(() => {
    if (!pendingOperations.length) {
      if (lookingAt === "host") {
        host.lookAt(player.absolutePosition);
      } else if (lookingAt === "rails") {
        if (lab === "Chill_Lab") {
          player.lookAt(rails.absolutePosition);
          const positionRails = rails.absolutePosition.clone();
          positionRails.y = 1.5;
          window.scene.activeCamera.setTarget(positionRails);
          window.scene.activeCamera.position = new BABYLON.Vector3(
            6.96,
            2.22,
            -4.25
          );
        } else {
          const positionProduct = product.absolutePosition.clone();
          positionProduct.y = 0;
          player.lookAt(positionProduct);
          positionProduct.y = 1.5;
          window.scene.activeCamera.setTarget(positionProduct);
          window.scene.activeCamera.position = new BABYLON.Vector3(
            6.96,
            2.22,
            -4.25
          );
        }
      } else {
        const newPosition = window.scene.activeCamera.position.clone();
        newPosition.y -= 1.5;
        host.lookAt(newPosition);
      }

      window.scene.render();
    }
  });

  window.addEventListener("resize", resizeCanvasThrottled);
  resizeCanvas();
};

const stopScene = () => {
  engine.stopRenderLoop();
  window.removeEventListener("resize", resizeCanvasThrottled);
  resizeTimeout = null;
  pendingOperations = [];
  canvasBJS = null;
};

const resizeCanvasThrottled = () => {
  if (resizeTimeout) {
    clearTimeout(resizeTimeout);
  }

  resizeTimeout = setTimeout(() => {
    resizeCanvas();
  }, 50);
};

const resizeCanvas = () => {
  const uniqueID = generateUniqueID();
  pendingOperations.push(uniqueID);

  engine.resize();

  pendingOperations = pendingOperations.filter((id) => id !== uniqueID);
};

const createMainCamera = () => {
  //rotate the camera behind the player
  const alpha = (0 * Math.PI) / 180;
  const beta = 1.25; // (75 * Math.PI) / 180
  const radius = 2.28;

  const playerPosition = playerPositions[lab];

  let target;

  if (playerPosition) {
    target = new BABYLON.Vector3(
      playerPosition[0],
      playerPosition[1] + 1.5,
      playerPosition[2]
    );
  }

  const camera = new BABYLON.ArcRotateCamera(
    "Main",
    alpha,
    beta,
    radius,
    target,
    window.scene,
    false
  );

  //standard camera setting
  camera.fov = 0.8;
  camera.wheelPrecision = 15;
  camera.pinchPrecision = 30;

  camera.checkCollisions = false;
  //make sure the keyboard keys controlling camera are different from those controlling player
  //here we will not use any keyboard keys to control camera
  camera.keysLeft = [];
  camera.keysRight = [];
  camera.keysUp = [];
  camera.keysDown = [];
  //how close can the camera come to player
  camera.lowerRadiusLimit = 2.28; // 1.5
  //how far can the camera go from the player
  camera.upperRadiusLimit = 2.28;
  //how top can the camera come to player
  camera.lowerBetaLimit = 1.25;
  //how down can the camera go from the player
  camera.upperBetaLimit = 2; // 1.55

  // camera.minZ = 1.7;
  // mainCam.lockedTarget = center.clone();
};

const loadEnvironment = () => {
  /*let light = new BABYLON.HemisphericLight(
    "light1",
    new BABYLON.Vector3(0, 1, 0),
    window.scene
  );
  light.intensity = 0.7;

  let light2 = new BABYLON.DirectionalLight(
    "light2",
    new BABYLON.Vector3(-1, -1, -1),
    window.scene
  );
  light2.position = new BABYLON.Vector3(0, 128, 0);
  light2.intensity = 0.7;
  */

  const url = `${BASE_URL}environment/env_${lab}.env`;
  const hdrTexture = BABYLON.CubeTexture.CreateFromPrefilteredData(
    url,
    window.scene
  );

  hdrTexture.rotationY = 0;
  window.scene.environmentTexture = hdrTexture;
  window.scene.environmentIntensity = 1;
};

const createRenderingPipeline = () => {
  pipeline = new BABYLON.DefaultRenderingPipeline(
    "defaultPipeline", // The name of the pipeline
    false, // Do you want the pipeline to use HDR texture?
    window.scene, // The scene instance
    window.scene.cameras // The list of cameras to be attached to
  );

  pipeline.samples = 8;
  pipeline.fxaaEnabled = true;

  pipeline.bloomEnabled = false; // false by default
  if (pipeline.bloomEnabled) {
    pipeline.bloomKernel = 62; // 64 by default
    pipeline.bloomScale = 1; // 0.5 by default
    pipeline.bloomThreshold = 1; // 0.9 by default
    pipeline.bloomWeight = 1; // 0.15 by default
  }
};

const processMeshes = () => {
  if (lab === "Chill_Lab") {
    window.scene.meshes.forEach((mesh) => {
      mesh.renderingGroupId = 1;

      if (mesh.name === "__root__") {
        mesh.name = "__root__Lab";
        mesh.id = "__root__Lab";
        mesh.checkCollisions = false;
        mesh.isPickable = false;
        mesh.alwaysSelectAsActiveMesh = false;
        mesh.doNotSyncBoundingInfo = true;
      } else if (
        ["P_Collider", "P_Floor", "P_AFC_SoundPanel_Colliders"].includes(
          mesh.name
        )
      ) {
        mesh.isVisible = mesh.name === "P_Floor" ? true : false;
        mesh.checkCollisions = true;
        mesh.isPickable = true;
        mesh.alwaysSelectAsActiveMesh = true;
        mesh.doNotSyncBoundingInfo = false;
        if (mesh.name === "P_Floor") {
          mesh.material.metallicF0Factor = 0;
          mesh.material.albedoColor =
            BABYLON.Color3.FromHexString("#DDDDDD").toLinearSpace();
        } else if (mesh.name === "P_AFC_SoundPanel_Colliders") {
          mesh.setEnabled(false);
        }
      } else if ("P_Cables_PowerFlow_Interior" === mesh.name) {
        mesh.material.albedoColor = BABYLON.Color3.Yellow();
        mesh.visibility = 0;
        mesh.setEnabled(false);
      } else if ("P_Cables_PowerFlow_Exterior" === mesh.name) {
        mesh.visibility = 0;
        mesh.setEnabled(false);
      } else if (
        [
          "P_AFC_NA_WaterFlow_Interior_Hot",
          "P_AFC_AD_WaterFlow_Interior_Hot",
        ].includes(mesh.name)
      ) {
        mesh.material.albedoColor =
          BABYLON.Color3.FromHexString("#E93440").toLinearSpace();
        mesh.setEnabled(false);
      } else if (
        [
          "P_AFC_NA_WaterFlow_Interior_Cold",
          "P_AFC_AD_WaterFlow_Interior_Cold",
        ].includes(mesh.name)
      ) {
        mesh.material.albedoColor =
          BABYLON.Color3.FromHexString("#33BBFF").toLinearSpace();
        mesh.setEnabled(false);
      } else if (["P_AFC_AD_Drop_Water"].includes(mesh.name)) {
        mesh.visibility = 0;
        mesh.material.albedoColor =
          BABYLON.Color3.FromHexString("#0099E6").toLinearSpace();
        mesh.setEnabled(false);
      } else if (
        [
          "P_AFC_NA_WaterFlow_ExteriorInferior",
          "P_AFC_NA_WaterFlow_ExteriorSuperior",
          "P_AFC_NA_Outlet_Connection",
          "P_AFC_NA_Inlet_Connection",
          "P_AFC_AD_WaterFlow_ExteriorInferior",
          "P_AFC_AD_WaterFlow_ExteriorSuperior",
          "P_AFC_AD_Outlet_Connection",
          "P_AFC_AD_Inlet_Connection",
          "P_Inside_Box",
        ].includes(mesh.name)
      ) {
        mesh.visibility = 0;
        mesh.setEnabled(false);
      } else if ("P_AirFlow_Hot" === mesh.name) {
        mesh.visibility = 0;
        mesh.isPickable = false;
        mesh.material.albedoColor =
          BABYLON.Color3.FromHexString("#E93440").toLinearSpace();
        mesh.setEnabled(false);
      } else if ("P_AirFlow_Cold" === mesh.name) {
        mesh.visibility = 0;
        mesh.isPickable = false;
        mesh.material.albedoColor =
          BABYLON.Color3.FromHexString("#33BBFF").toLinearSpace();
        mesh.setEnabled(false);
      } else if ("P_Rails" === mesh.name) {
        mesh.checkCollisions = true;
        mesh.isPickable = true;
        mesh.alwaysSelectAsActiveMesh = true;
        mesh.doNotSyncBoundingInfo = false;
        initialPositions.RAILS = mesh.position.x;
      } else if (
        [
          "P_AFC_NA",
          "P_AFC_AD",
          "P_AFC_AD_VCoils",
          "P_AFC_NA_VCoils",
          "P_AFC_AD_Pads",
        ].includes(mesh.name)
      ) {
        mesh.isPickable = true;
      } else if (
        [
          "P_Control_Room_Window_Clear",
          "P_Control_Room_Window_Dark",
          "P_Beauty_Collider",
        ].includes(mesh.name)
      ) {
        mesh.material.metallicF0Factor = 0;
        if (mesh.name === "P_Control_Room_Window_Clear") {
          mesh.setEnabled(false);
        }
      } else if ("P_AFCPortal" === mesh.name) {
        // mesh.renderingGroupId = 0;
        mesh.material.backFaceCulling = true;
      } else {
        mesh.isPickable = false;
      }

      // if (mesh.material) {
      //   mesh.material.backFaceCulling = true;
      // }
    });

    const product_AFC_NA = window.scene.getTransformNodeByName("AFC_NA_Group");
    const product_AFC_AD = window.scene.getTransformNodeByName("AFC_AD_Group");

    initialPositions.AFC_NA = product_AFC_NA.position.x;
    initialPositions.AFC_AD = product_AFC_AD.position.x;

    product_AFC_NA.position.x -= 15;
    product_AFC_AD.position.x -= 15;

    product_AFC_NA.setEnabled(false);
    product_AFC_AD.setEnabled(false);

    const AFC_SoundPanel =
      window.scene.getTransformNodeByName("AFC_SoundPanel");
    // finalPanelPosition = AFC_SoundPanel.position.y;
    // AFC_SoundPanel.position.y += 2.54;
    // initialPanelPosition = AFC_SoundPanel.position.y;

    for (let index = 1; index <= 10; index++) {
      window.scene.getMeshByName(`P_AFC_SoundPanel_${index}`).visibility = 0;
    }

    AFC_SoundPanel.setEnabled(false);

    // const P_AFCPortal = window.scene.getMeshByName("P_AFCPortal");

    // const P_AFCPortal_clone = P_AFCPortal.clone("P_AFCPortal_clone");
    // P_AFCPortal_clone.renderingGroupId = 1;
    // P_AFCPortal_clone.position.z = 14;

    window.scene.imageProcessingConfiguration.exposure = 1.2;
  } else {
    window.scene.meshes.forEach((mesh) => {
      mesh.renderingGroupId = 1;

      if (mesh.name === "__root__") {
        mesh.name = "__root__Lab";
        mesh.id = "__root__Lab";
        mesh.checkCollisions = false;
        mesh.isPickable = false;
        mesh.alwaysSelectAsActiveMesh = false;
        mesh.doNotSyncBoundingInfo = true;
      } else if (["P_Collider", "P_Floor"].includes(mesh.name)) {
        mesh.isVisible = mesh.name === "P_Floor";
        mesh.checkCollisions = true;
        mesh.isPickable = true;
        mesh.alwaysSelectAsActiveMesh = true;
        mesh.doNotSyncBoundingInfo = false;
        if (mesh.name === "P_Floor") {
          mesh.material.metallicF0Factor = 0.4;
          // TODO check
          mesh.material.albedoColor =
            BABYLON.Color3.FromHexString("#DDDDDD").toLinearSpace();
        }
      } else if (
        [
          "P_CWA_Cables_PowerFlow_Interior",
          "P_PDX_Cables_PowerFlow_Interior",
        ].includes(mesh.name)
      ) {
        mesh.material.albedoColor = BABYLON.Color3.Yellow();
        mesh.visibility = 0;
        mesh.setEnabled(false);
      } else if (
        [
          "P_CWA_Cables_PowerFlow_Exterior",
          "P_PDX_Cables_PowerFlow_Exterior",
          "P_CWA_Cables_Cover",
          "P_PDX_Cables_Cover",
          "P_CWA_Duct",
          "P_PDX_Duct",
          "P_PDX_Heat_Exchanger",
        ].includes(mesh.name)
      ) {
        mesh.visibility = 0;
        mesh.setEnabled(false);

        if (mesh.name === "P_PDX_Duct") {
          mesh.material.metallicF0Factor = 0.3;
        } else if (mesh.name === "P_CWA_Duct") {
          mesh.material.metallicF0Factor = 0;
        }
      } else if (
        [
          "P_CWA_WaterFlow_Interior_Hot",
          "P_PDX_WaterFlow_Interior_Hot",
        ].includes(mesh.name)
      ) {
        mesh.material.albedoColor =
          BABYLON.Color3.FromHexString("#E93440").toLinearSpace();
        mesh.setEnabled(false);
      } else if (
        [
          "P_CWA_WaterFlow_Interior_Cold",
          "P_PDX_WaterFlow_Interior_Cold",
        ].includes(mesh.name)
      ) {
        mesh.material.albedoColor =
          BABYLON.Color3.FromHexString("#33BBFF").toLinearSpace();
        mesh.setEnabled(false);
      } else if (
        [
          "P_CWA_WaterFlow_ExteriorInferior",
          "P_CWA_WaterFlow_ExteriorSuperior",
          "P_PDX_WaterFlow_ExteriorInferior",
          "P_PDX_WaterFlow_ExteriorSuperior",
        ].includes(mesh.name)
      ) {
        mesh.visibility = 0;
        mesh.setEnabled(false);
      } else if (
        ["P_CWA_AirFlow_Hot", "P_PDX_AirFlow_Hot"].includes(mesh.name)
      ) {
        mesh.visibility = 0;
        mesh.isPickable = false;
        mesh.material.albedoColor =
          BABYLON.Color3.FromHexString("#E93440").toLinearSpace();
        mesh.setEnabled(false);
      } else if (
        ["P_CWA_AirFlow_Cold", "P_PDX_AirFlow_Cold"].includes(mesh.name)
      ) {
        mesh.visibility = 0;
        mesh.isPickable = false;
        mesh.material.albedoColor =
          BABYLON.Color3.FromHexString("#33BBFF").toLinearSpace();
        mesh.setEnabled(false);
      } else if ("P_Portal" === mesh.name) {
        mesh.renderingGroupId = 0;
        mesh.material.backFaceCulling = true;
      } else if (["P_CWA_Exterior", "P_PDX_Exterior"].includes(mesh.name)) {
        mesh.material.metallicF0Factor = 0;
        mesh.isPickable = true;
      } else if (
        [
          "P_Control_Room_Window_Clear",
          "P_Control_Room_Window_Dark",
          "P_Beauty_Collider",
        ].includes(mesh.name)
      ) {
        mesh.material.metallicF0Factor = 0;
        if (mesh.name === "P_Control_Room_Window_Clear") {
          mesh.setEnabled(false);
        }
      } else if ("P_ControlRoom" === mesh.name) {
        mesh.material.metallicF0Factor = 0;
        gl.addExcludedMesh(mesh);
      } else if (
        [
          "P_Thermo_Regulation",
          "P_AirFlow_Connection",
          "P_Structure",
          "P_PDX_Platform",
          "P_CWA_Platform",
        ].includes(mesh.name)
      ) {
        mesh.material.metallicF0Factor = 0;

        if (mesh.name === "P_Structure") {
          mesh.material.metallic = 0.5;
        } else if (mesh.name === "P_AirFlow_Connection") {
          mesh.material.albedoColor =
            BABYLON.Color3.FromHexString("#BFBFBF").toLinearSpace();
        }
      } else if ("P_Props" === mesh.name) {
        mesh.material.metallicF0Factor = 0.4;
        gl.addExcludedMesh(mesh);
      } else if ("P_Water_Tottem" === mesh.name) {
        mesh.material.metallicF0Factor = 0.5;
      } else if ("P_Power_Connection" === mesh.name) {
        mesh.material.metallicF0Factor = 0.7;
      } else if ("P_Pipelines" === mesh.name) {
        mesh.material.metallic = 0;
      } else {
        // TODO check
        mesh.isPickable = false;
      }

      // if (mesh.material) {
      //   mesh.material.backFaceCulling = true;
      // }
    });

    const product_CWA = window.scene.getTransformNodeByName("CWA_Group");
    initialPositions.CWA = product_CWA.position.z;
    product_CWA.position.z += 15;
    product_CWA.setEnabled(false);

    const product_PDX = window.scene.getTransformNodeByName("PDX_Group");
    initialPositions.PDX = product_PDX.position.z;
    product_PDX.position.z += 15;
    product_PDX.setEnabled(false);

    const P_Portal = window.scene.getMeshByName("P_Portal");

    const P_Portal_clone = P_Portal.clone("P_Portal_clone");
    P_Portal_clone.renderingGroupId = 1;
    P_Portal_clone.position.z = 14;

    window.scene.imageProcessingConfiguration.contrast = 1.5;
  }
};

const pathFlyCamera = [
  {
    // Start
    frame: 0,
    target: new BABYLON.Vector3(3.5, 1.5, -3),
    alpha: (-90 * Math.PI) / 180, // Look front
  },
  {
    // Door
    frame: 1,
    target: new BABYLON.Vector3(7.5, 1.5, 0),
    alpha: (-180 * Math.PI) / 180, // Look front
  },
  {
    // Initial
    frame: 2,
    target: new BABYLON.Vector3(7.5, 1.5, 0),
    alpha: 0, // Look front
  },
  {
    // CTA product start
    frame: 3,
    showHotspot: "product",
    target: new BABYLON.Vector3(4, 1.5, 0), // -2.5
    alpha: 0, // Look front (-90 * Math.PI) / 180
  },
  {
    // CTA product end
    frame: 5,
    hideHotspot: "product",
    target: new BABYLON.Vector3(4, 1.5, 0), // -2.5
    alpha: 0, // Look right (-90 * Math.PI) / 180
  },
  {
    // Left bottom
    frame: 6,
    target: new BABYLON.Vector3(4, 1.5, -2.5),
    alpha: (45 * Math.PI) / 180, // (-45 * Math.PI) / 180
  },
  {
    // CTA control room start
    frame: 8,
    showHotspot: "control",
    target: new BABYLON.Vector3(0, 1.5, -2.5),
    alpha: (90 * Math.PI) / 180, // Look left
  },
  {
    // CTA control room end
    frame: 10,
    hideHotspot: "control",
    target: new BABYLON.Vector3(0, 1.5, -2.5),
    alpha: (90 * Math.PI) / 180, // Look left
  },
  {
    // Going
    frame: 12,
    target: new BABYLON.Vector3(-2, 1.5, -2.5),
    alpha: 0, // Look left
  },
  {
    // CTA water flow start
    frame: 13,
    showHotspot: "hydraulic",
    target: new BABYLON.Vector3(-4, 1.5, -2.5),
    alpha: (90 * Math.PI) / 180, // Look left
  },
  {
    // CTA water flow end
    frame: 15,
    hideHotspot: "hydraulic",
    target: new BABYLON.Vector3(-4, 1.5, -2.5),
    alpha: (90 * Math.PI) / 180, // Look left
  },
  {
    // Left top
    frame: 17,
    target: new BABYLON.Vector3(-7.5, 1.5, -2.5),
    alpha: (-90 * Math.PI) / 180, // Look front
  },
  {
    // Middle top down
    frame: 18,
    target: new BABYLON.Vector3(-7.5, 1.5, -1),
    alpha: (-90 * Math.PI) / 180, // Look front (-180 * Math.PI) / 180
  },
  {
    // Middle top up
    frame: 19,
    showHotspot: "thermoregulation",
    target: new BABYLON.Vector3(-7.5, 4.5, 0),
    alpha: (-180 * Math.PI) / 180, // Look front
  },
  {
    // Middle top up
    frame: 21,
    hideHotspot: "thermoregulation",
    target: new BABYLON.Vector3(-7.5, 4.5, 0),
    alpha: (-180 * Math.PI) / 180, // Look front
  },
  {
    // Middle top down
    frame: 22,
    target: new BABYLON.Vector3(-7.5, 1.5, 1),
    alpha: (-90 * Math.PI) / 180, // Look front (-180 * Math.PI) / 180
  },
  {
    // Right top
    frame: 23,
    target: new BABYLON.Vector3(-7.5, 1.5, 2.5),
    alpha: (-180 * Math.PI) / 180, // Look back
  },
  {
    // CTA power
    frame: 25,
    showHotspot: "power",
    target: new BABYLON.Vector3(0.5, 1.5, 2.5),
    alpha: (-90 * Math.PI) / 180, // Look right
  },
  {
    // CTA power
    frame: 27,
    hideHotspot: "power",
    target: new BABYLON.Vector3(0.5, 1.5, 2.5),
    alpha: (-90 * Math.PI) / 180, // Look right
  },
  {
    // Right bottom
    frame: 29,
    target: new BABYLON.Vector3(7.5, 1.5, 2.5),
    alpha: (0 * Math.PI) / 180, // Look back
  },
  {
    // Final
    frame: 30,
    showLabels: true,
    target: new BABYLON.Vector3(7.5, 1.5, 0),
    alpha: 0, // Look front
  },
  {
    // Final
    frame: 32,
    hideLabels: true,
    target: new BABYLON.Vector3(7.5, 1.5, 0),
    alpha: 0, // Look front
  },
  {
    // Final
    frame: 33,
    target: new BABYLON.Vector3(7.5, 1.5, 0),
    alpha: 0, // Look front
  },
];

let animatingCamera = false;

const aerialView = async (speed = 0.5) => {
  if (process.env.NODE_ENV === "development") {
    speed *= window.speedFactor || 1;
  }

  const camera = window.scene.getCameraByName("Fly");

  if (animatingCamera) {
    // We are currently in one animation, so we end the first one to proceed to the next one
    window.scene.stopAnimation(camera);
  }

  window.scene.activeCamera = camera;
  camera.attachControl(canvasBJS, false);

  displayHost(false);
  displayPlayer(false);

  // About to animate. Set animating to true.
  animatingCamera = true;

  let indexPaths = 0;

  const {
    keysTargets,
    keysAlphas,
    keysShow,
    keysHide,
    keysShowLabels,
    keysHideLabels,
  } = pathFlyCamera.reduce(
    (keys, frame) => {
      keys.keysTargets[indexPaths].push({
        frame: frame.frame,
        value: frame.target,
      });

      keys.keysAlphas[indexPaths].push({
        frame: frame.frame,
        value: frame.alpha,
      });

      if (frame.showHotspot) {
        indexPaths++;

        keys.keysTargets.push([
          {
            frame: frame.frame,
            value: frame.target,
          },
        ]);

        keys.keysAlphas.push([
          {
            frame: frame.frame,
            value: frame.alpha,
          },
        ]);

        keys.keysShowLabels.push(null);
        keys.keysHideLabels.push(null);

        keys.keysShow.push(frame.showHotspot);

        if (frame.hideHotspot) {
          keys.keysHide.push(frame.hideHotspot);
        } else {
          keys.keysHide.push(null);
        }
      } else if (frame.hideHotspot) {
        indexPaths++;

        keys.keysTargets.push([
          {
            frame: frame.frame,
            value: frame.target,
          },
        ]);

        keys.keysAlphas.push([
          {
            frame: frame.frame,
            value: frame.alpha,
          },
        ]);

        keys.keysShow.push(null);
        keys.keysShowLabels.push(null);
        keys.keysHideLabels.push(null);

        keys.keysHide.push(frame.hideHotspot);
      } else if (frame.showLabels || frame.hideLabels) {
        indexPaths++;

        keys.keysTargets.push([
          {
            frame: frame.frame,
            value: frame.target,
          },
        ]);

        keys.keysAlphas.push([
          {
            frame: frame.frame,
            value: frame.alpha,
          },
        ]);

        keys.keysShow.push(null);
        keys.keysHide.push(null);

        keys.keysShowLabels.push(frame.showLabels);
        keys.keysHideLabels.push(frame.hideLabels);
      }

      return keys;
    },
    {
      keysTargets: [[]],
      keysAlphas: [[]],
      keysShow: [null],
      keysHide: [null],
      keysShowLabels: [null],
      keysHideLabels: [null],
    }
  );

  const acTarget = new BABYLON.Animation(
    "acTarget",
    "target",
    1,
    BABYLON.Animation.ANIMATIONTYPE_VECTOR3,
    BABYLON.Animation.ANIMATIONLOOPMODE_CONSTANT
  );

  const acAlpha = new BABYLON.Animation(
    "acAlpha",
    "alpha",
    1,
    BABYLON.Animation.ANIMATIONTYPE_FLOAT,
    BABYLON.Animation.ANIMATIONLOOPMODE_CONSTANT
  );

  for (let index = 0; index < keysTargets.length; index++) {
    const keysTarget = keysTargets[index];
    const keysAlpha = keysAlphas[index];
    const show = keysShow[index];
    const hide = keysHide[index];
    const showLabel = keysShowLabels[index];
    const hideLabel = keysHideLabels[index];

    if (show) {
      callbackDisplayHotspot(show, true);
      showAnimation(`hotspot_${show}`, true, 0.5);
    }

    if (hide) {
      callbackDisplayHotspot(hide, false);
    }

    if (showLabel) {
      callbackDisplayHotspot("product", true);
      callbackDisplayHotspot("control", true);
      callbackDisplayHotspot("hydraulic", true);
      callbackDisplayHotspot("thermoregulation", true);
      callbackDisplayHotspot("power", true);
    }

    if (hideLabel) {
      callbackDisplayHotspot("product", false);
      callbackDisplayHotspot("control", false);
      callbackDisplayHotspot("hydraulic", false);
      callbackDisplayHotspot("thermoregulation", false);
      callbackDisplayHotspot("power", false);
    }

    acTarget.setKeys(keysTarget);

    acAlpha.setKeys(keysAlpha);

    await new Promise((resolve) => {
      window.scene.beginDirectAnimation(
        camera,
        [acTarget, acAlpha],
        keysAlpha[0].frame,
        keysAlpha[keysAlpha.length - 1].frame,
        false,
        speed,
        () => {
          resolve();
        }
      );
    });
  }

  animatingCamera = false;

  displayHost(true);
  displayPlayer(true);

  setCamera("Welcome");
};

const createFlyCamera = () => {
  //rotate the camera behind the player
  const alpha = (-90 * Math.PI) / 180;
  const beta = (90 * Math.PI) / 180; // (75 * Math.PI) / 180
  const radius = 2.28;

  let target;

  const playerPosition = playerPositions[lab];

  if (playerPosition) {
    target = new BABYLON.Vector3(
      playerPosition[0],
      playerPosition[1] + 1.5,
      playerPosition[2]
    );
  }

  const camera = new BABYLON.ArcRotateCamera(
    "Fly",
    alpha,
    beta,
    radius,
    target,
    window.scene,
    false
  );

  //standard camera setting
  camera.fov = 0.8;
  camera.wheelPrecision = 0;
  camera.pinchPrecision = 0;

  camera.checkCollisions = false;
  //make sure the keyboard keys controlling camera are different from those controlling player
  //here we will not use any keyboard keys to control camera
  camera.inputs.clear();
  //how close can the camera come to player
  camera.lowerRadiusLimit = 2.28;
  //how far can the camera go from the player
  camera.upperRadiusLimit = 2.28;
  //how close can the camera come to player
  camera.lowerBetaLimit = (90 * Math.PI) / 180;
  //how far can the camera go from the player
  camera.upperBetaLimit = (90 * Math.PI) / 180;
  // This attaches the camera to the canvas
  // camera.attachControl(canvasBJS, false);
};

const createHelperCamera = () => {
  //rotate the camera behind the player

  const alpha = (-35 * Math.PI) / 180;
  const beta = 1.37; // (75 * Math.PI) / 180
  const radius = 2.28;

  let target;

  const helperCameraTarget = helperCameraTargets[lab];

  if (helperCameraTarget) {
    target = new BABYLON.Vector3(
      helperCameraTarget[0],
      helperCameraTarget[1],
      helperCameraTarget[2]
    );
  } else {
    console.error("Add position for helper camera on this lab");
  }

  const camera = new BABYLON.ArcRotateCamera(
    "Helper",
    alpha,
    beta,
    radius,
    target,
    window.scene,
    false
  );

  //standard camera setting
  camera.fov = 0.8;

  camera.checkCollisions = false;
  //make sure the keyboard keys controlling camera are different from those controlling player
  //here we will not use any keyboard keys to control camera
  camera.inputs.clear();
  //how close can the camera come to player
  camera.lowerRadiusLimit = null;
  //how far can the camera go from the player
  camera.upperRadiusLimit = null;
  //how close can the camera come to player
  camera.lowerBetaLimit = null;
  //how far can the camera go from the player
  camera.upperBetaLimit = null;
  // This attaches the camera to the canvas
  // camera.attachControl(canvasBJS, false);
  camera.minZ = 0.4;
};

const createWelcomeCamera = () => {
  //rotate the camera behind the player

  const alpha = 1.1;
  const beta = 1.3;
  const radius = 3;

  let target;

  const welcomeCameraTarget = welcomeCameraTargets[lab];

  if (welcomeCameraTarget) {
    target = new BABYLON.Vector3(
      welcomeCameraTarget[0],
      welcomeCameraTarget[1],
      welcomeCameraTarget[2]
    );
  } else {
    console.error("Add position for helper camera on this lab");
  }

  const camera = new BABYLON.ArcRotateCamera(
    "Welcome",
    alpha,
    beta,
    radius,
    target,
    window.scene,
    false
  );

  camera.fov = 0.8;

  camera.checkCollisions = false;

  camera.inputs.clear();

  camera.lowerRadiusLimit = null;
  camera.upperRadiusLimit = null;

  camera.lowerAlphaLimit = null;
  camera.upperAlphaLimit = null;

  camera.lowerBetaLimit = null;
  camera.upperBetaLimit = null;
};

const setCamera = (name) => {
  const camera = window.scene.getCameraByName(name);

  window.scene.activeCamera = camera;
  camera.attachControl(canvasBJS, false);
};

const generateUniqueID = () => {
  return Math.floor(Math.random() * Math.floor(Math.random() * Date.now()));
};

const createDot = ([x, y, z], name = "test") => {
  const dot = BABYLON.MeshBuilder.CreateSphere(name, { diameter: 0.02 });
  dot.setAbsolutePosition(new BABYLON.Vector3(x, y, z));
  dot.renderingGroupId = 1;
  dot.isPickable = false;
  return dot;
};

const cameraHelper = (cameraName, [x, y, z], [x2, y2, z2], newRadius, newY) => {
  const camera = window.scene.getCameraByName(cameraName);
  window.scene.activeCamera = camera;

  camera.attachControl(canvasBJS, false);
  camera.wheelPrecision = 80;
  camera.pinchPrecision = 100;

  if (x !== undefined && y !== undefined && z !== undefined) {
    camera.setPosition(new BABYLON.Vector3(x, y, z));
  }

  if (x2 !== undefined && y2 !== undefined && z2 !== undefined) {
    camera.setTarget(new BABYLON.Vector3(x2, y2, z2));
  }

  if (newRadius) {
    camera.radius = newRadius;
  }

  if (newY) {
    camera.targetScreenOffset.y = newY;
  }

  console.warn("3D-Configurator", "Values");
  console.warn("3D-Configurator", "alpha:", camera.alpha);
  console.warn("3D-Configurator", "beta:", camera.beta);
  console.warn("3D-Configurator", "radius:", camera.radius);

  console.warn("3D-Configurator", "Position");
  console.warn("3D-Configurator", "x:", camera.position.x);
  console.warn("3D-Configurator", "y:", camera.position.y);
  console.warn("3D-Configurator", "z:", camera.position.z);

  console.warn("3D-Configurator", "Target");
  console.warn("3D-Configurator", "x:", camera.target.x);
  console.warn("3D-Configurator", "y:", camera.target.y);
  console.warn("3D-Configurator", "z:", camera.target.z);

  return camera;
};

function sleep(ms) {
  if (process.env.NODE_ENV === "development") {
    ms /= window.speedFactor || 1;
  }

  return new Promise((resolve) => {
    setTimeout(() => {
      resolve();
    }, ms);
  });
}

function debugMode() {
  window.scene.debugLayer.show({
    embedMode: true,
  });
}

window.debugMode = debugMode;

export {
  createScene,
  runScene,
  stopScene,
  resizeCanvas,
  createDot,
  cameraHelper,
  createMainCamera,
  loadProduct,
  hideProduct,
  powerOnProduct,
  powerOffProduct,
  startTest,
  sleep,
  showAnimation,
  loadAvatar,
  aerialView,
  createHotspot,
  getPosition,
  getPositionMesh,
  calculatePixel,
  updateHotspotPosition,
  lightAnimation,
  setCancelAnimation,
  loadControlRoom,
  setCamera,
  securePlayer,
  dropDownAnimation, // Not used
  soundPanelAnimation, // Not used
  changePerspective,
  actionCTA,
  setSkipLoad,
  playSound, // Not used
  loadCompressor, // Not used
};
